import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  InputGroup,
  Spinner,
  Accordion,
  ProgressBar,
  Tab,
  Tabs,
  OverlayTrigger,
  Tooltip,
  Alert,
  Modal,
  Dropdown,
  Collapse,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faHome,
  faBriefcase,
  faWrench,
  faCheckCircle,
  faTimesCircle,
  faClock,
  faCalendarAlt,
  faMapMarkerAlt,
  faBuilding,
  faChevronDown,
  faChevronUp,
  faFileAlt,
  faSolarPanel,
  faExclamationTriangle,
  faInfoCircle,
  faClipboardList,
  faUser,
  faPoundSign,
  faCertificate,
  faFilter,
  faTimes,
  faEye,
  faListAlt,
  faDatabase,
  faEyeSlash,
  faToggleOn,
  faToggleOff,
  faQuestionCircle,
  faLightbulb,
  faLeaf,
  faBolt,
  faArrowRight,
  faFileDownload,
  faPaperPlane,
  faTag,
} from "@fortawesome/free-solid-svg-icons";
import measuresApiService from "../services/measuresApiService";
import { DEMO_DATA } from "../data/measuresTrackerDemoData";

/* ─── EXPORT HELPERS ─────────────────────────────────────────── */

const exportJobSummary = (groupedData) => {
  const rows = [];
  rows.push(["Property", "Postcode", "Job ID", "External CRM ID", "Company", "Scheme Name", "Created By", "Job Created", "Shortlisted", "Submitted", "In Progress", "Installed", "Signed Off", "Cancelled", "Total Measures"]);
  Object.values(groupedData).forEach((property) => {
    Object.values(property.jobs).forEach((job) => {
      const realMeasures = job.measures.filter((m) => !m.IsJobOnly);
      const schemeName = getSchemeLabel(job.measures.find((measure) => getSchemeLabel(measure)));
      rows.push([
        property.address || "",
        property.postcode || "",
        job.jobUId || job.jobId?.substring(0, 8) || "",
        job.externalCrmId || "",
        job.companyName || "",
        schemeName,
        job.createdByName || "",
        job.jobCreatedUtc ? new Date(job.jobCreatedUtc).toLocaleDateString("en-GB") : "",
        realMeasures.filter((m) => m.IsRecommendation).length,
        realMeasures.filter((m) => !m.CancelledUtc && !m.InstallUtc && !m.HandoverUtc && !m.SignedOffUtc && !(Number(m.StageCount) > 0)).length,
        realMeasures.filter((m) => !m.CancelledUtc && !m.InstallUtc && !m.HandoverUtc && !m.SignedOffUtc && Number(m.StageCount) > 0).length,
        realMeasures.filter((m) => (m.InstallUtc || m.HandoverUtc) && !m.SignedOffUtc && !m.CancelledUtc).length,
        realMeasures.filter((m) => m.SignedOffUtc && !m.CancelledUtc).length,
        realMeasures.filter((m) => m.CancelledUtc).length,
        realMeasures.length,
      ]);
    });
  });
  return { rows, cols: [{ wch: 30 }, { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 26 }, { wch: 15 }, { wch: 13 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }] };
};

const exportMeasures = (groupedData) => {
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB") : "";
  const smd = (simple, key) => {
    const v = simple?.[key];
    return (v === null || v === undefined) ? "" : String(v).trim();
  };
  const measureStatus = (measure) => {
    if (measure.IsRecommendation) return "Shortlisted";
    if (measure.CancelledUtc)     return "Cancelled";
    if (measure.SignedOffUtc)     return "Signed Off";
    if (measure.InstallUtc || measure.HandoverUtc) return "Installed";
    if (Number(measure.StageCount) > 0) return "In Progress";
    const fromMap = MEASURE_STATUS_MAP[measure.MeasureStatus];
    return fromMap ? fromMap.label : "Submitted";
  };

  const rows = [];
  rows.push([
    // Identity
    "Address", "Postcode", "Job Ref", "External CRM ID",
    // Job context
    "Company", "Job Status", "Lot / Funding", "Scheme Name", "Assigned To", "Job Created",
    // Measure identity
    "Measure Name", "ECO Measure Name", "Measure Type", "Work Description",
    // Status
    "Status", "Project Stage",
    // Key dates
    "Install Date", "Handover Date", "Signed Off Date", "Cancelled Date",
    // Survey-derived condition and SAP hints
    "Damp/Mould Identified", "HHSRS Issue Noted", "Survey Current SAP", "Survey Potential SAP", "Survey Wall Type", "Survey Built Form",
    // People
    "Signed Off By", "Cancelled By", "Created By", "Updated By", "Last Updated",
    // Financial & refs
    "Cost (£)", "TrustMark UMR", "Installation Ref",
    // Product details
    "Manufacturer", "Model", "Product Units", "MCS Certificate", "Operative Cert Ref", "ECO Scheme",
    // Counts
    "Evidence Count", "Stage Count",
  ]);

  Object.values(groupedData).forEach((property) => {
    Object.values(property.jobs).forEach((job) => {
      const jobStatus = JOB_STATUS_MAP[job.jobStatusId]?.label || (job.jobStatusId != null ? `Status ${job.jobStatusId}` : "");
      job.measures.filter((measure) => !measure.IsJobOnly).forEach((measure) => {
        const simple = typeof measure.SimpleMeasureDetails === "string"
          ? (() => { try { return JSON.parse(measure.SimpleMeasureDetails); } catch { return {}; } })()
          : measure.SimpleMeasureDetails || {};
        const displayName = measure.PasMeasureName || smd(simple, "pasMeasureName") || smd(simple, "workDescription") || measure.MeasureName || "";
        rows.push([
          // Identity
          property.address || "",
          property.postcode || "",
          job.jobUId || job.jobId?.substring(0, 8) || "",
          job.externalCrmId || measure.ExternalCrmId || "",
          // Job context
          job.companyName || measure.CompanyName || "",
          jobStatus,
          measure.LotName || job.lotName || "",
          getSchemeLabel(measure) || getSchemeLabel(job.measures.find((jobMeasure) => getSchemeLabel(jobMeasure))),
          job.createdByName || measure.CreatedByName || "",
          fmtDate(job.jobCreatedUtc),
          // Measure identity
          displayName,
          measure.EcoMeasureName || "",
          measure.PasMeasureType || smd(simple, "pasMeasureType") || "",
          smd(simple, "workDescription") || smd(simple, "pasMeasureName") || "",
          // Status
          measureStatus(measure),
          measure.ProjectStageName || "",
          // Key dates
          fmtDate(measure.InstallUtc),
          fmtDate(measure.HandoverUtc),
          fmtDate(measure.SignedOffUtc),
          fmtDate(measure.CancelledUtc),
          measure.SurveyHasDampMouldIssue ? "Yes" : "No",
          measure.SurveyHasHHSRSIssue ? "Yes" : "No",
          measure.SurveyCurrentSAP != null ? Number(measure.SurveyCurrentSAP) : "",
          measure.SurveyPotentialSAP != null ? Number(measure.SurveyPotentialSAP) : "",
          measure.SurveyWallType || "",
          measure.SurveyBuiltForm || "",
          // People
          measure.SignedOffByName || "",
          measure.CancelledByName || "",
          measure.CreatedByName || "",
          measure.UpdatedByName || "",
          fmtDate(measure.UpdatedUtc),
          // Financial & refs
          measure.InstallationCost != null ? Number(measure.InstallationCost) : "",
          measure.TrustMarkUmr || "",
          measure.MeasureReference || "",
          // Product details
          smd(simple, "productManufacturer"),
          smd(simple, "productModel"),
          smd(simple, "productUnits"),
          smd(simple, "mcsCertificateNumber"),
          smd(simple, "operativeCertificationReference"),
          smd(simple, "ecoScheme"),
          // Counts
          measure.EvidenceCount ?? 0,
          measure.StageCount ?? 0,
        ]);
      });
    });
  });

  return {
    rows,
    cols: [
      { wch: 35 }, { wch: 12 }, { wch: 12 }, // address, postcode, job ref
      { wch: 22 }, { wch: 14 }, { wch: 22 }, { wch: 26 }, { wch: 18 }, { wch: 13 }, // company, job status, lot, scheme, assigned to, job created
      { wch: 30 }, { wch: 28 }, { wch: 26 }, { wch: 36 }, // measure name, eco name, type, work desc
      { wch: 14 }, { wch: 22 }, // status, stage
      { wch: 13 }, { wch: 13 }, { wch: 13 }, { wch: 13 }, // dates
      { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 24 }, { wch: 20 }, // survey hints
      { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 13 }, // people + updated
      { wch: 14 }, { wch: 18 }, { wch: 18 }, // cost, umr, ref
      { wch: 26 }, { wch: 26 }, { wch: 14 }, { wch: 22 }, { wch: 22 }, { wch: 16 }, // product
      { wch: 13 }, { wch: 11 }, // counts
    ],
  };
};

const exportAppointments = (groupedData) => {
  const rows = [];
  rows.push(["Property", "Job ID", "Scheme Name", "Appointment Date", "Status", "Type", "Resource", "Company", "Notes"]);
  Object.values(groupedData).forEach((property) => {
    Object.values(property.jobs).forEach((job) => {
      const schemeName = getSchemeLabel(job.measures.find((measure) => getSchemeLabel(measure)));
      if (job.jobInsights?.appointments?.all && Array.isArray(job.jobInsights.appointments.all)) {
        job.jobInsights.appointments.all.forEach((apt) => {
          rows.push([
            property.address || "",
            job.jobUId || job.jobId?.substring(0, 8) || "",
            schemeName,
            apt.AppointmentDateTime ? new Date(apt.AppointmentDateTime).toLocaleDateString("en-GB") : "",
            { 3: "Booked", 4: "Completed", 5: "Cancelled" }[apt.AppointmentStatus] || `Status ${apt.AppointmentStatus}`,
            { 1: "Home Survey", 2: "Other", 3: "Admin / Follow-up" }[apt.AppointmentType] || `Type ${apt.AppointmentType}`,
            apt.ResourceName || "",
            apt.CompanyName || "",
            (apt.Notes || "").substring(0, 100),
          ]);
        });
      }
    });
  });
  return { rows, cols: [{ wch: 30 }, { wch: 12 }, { wch: 26 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 40 }] };
};

/* ─── STATUS HELPERS ─────────────────────────────────────────── */

// PAS Hub measure status enum (matches ProjectStageStatus in PAS Hub codebase)
const MEASURE_STATUS_MAP = {
  1:  { label: "New",                   color: "secondary", icon: faFileAlt     },
  2:  { label: "RC Instructed",         color: "info",      icon: faPaperPlane  },
  3:  { label: "Awaiting Installation", color: "warning",   icon: faClock       },
  4:  { label: "Installation Complete", color: "success",   icon: faCheckCircle },
  5:  { label: "Awaiting Lodgement",    color: "info",      icon: faClock       },
  6:  { label: "Lodged",                color: "success",   icon: faCertificate },
  9:  { label: "Installation Booked",   color: "primary",   icon: faWrench      },
  10: { label: "Completed",             color: "success",   icon: faCheckCircle },
};

// Colour map for inferred lifecycle labels (date-derived, highest priority)
const LIFECYCLE_COLOR_MAP = {
  "Cancelled":       { color: "danger",  icon: faTimesCircle },
  "Signed Off":      { color: "success", icon: faCertificate },
  "Part Signed Off": { color: "info",    icon: faCertificate },
  "Installed":       { color: "success", icon: faCheckCircle },
  "In Progress":     { color: "warning", icon: faClock },
};

/**
 * Infer a lifecycle label for a single measure from its date fields.
 * CancelledUtc beats everything; SignedOffUtc beats install; otherwise
 * fall back to install dates, then to the numeric MEASURE_STATUS_MAP.
 */
const getMeasureLifecycleBadge = (measure) => {
  let entry = null;
  if (measure.CancelledUtc) {
    entry = { label: "Cancelled",   ...LIFECYCLE_COLOR_MAP["Cancelled"]   };
  } else if (measure.SignedOffUtc) {
    entry = { label: "Signed Off",  ...LIFECYCLE_COLOR_MAP["Signed Off"]  };
  } else if (measure.InstallUtc || measure.HandoverUtc) {
    entry = { label: "Installed",   ...LIFECYCLE_COLOR_MAP["Installed"]   };
  } else {
    entry = MEASURE_STATUS_MAP[measure.MeasureStatus] ||
            { label: `Status ${measure.MeasureStatus}`, color: "secondary", icon: null };
  }
  return (
    <Badge bg={entry.color} className="px-2 py-1">
      {entry.icon && <FontAwesomeIcon icon={entry.icon} className="me-1" />}
      {entry.label}
    </Badge>
  );
};

/** Pure string version of getMeasureLifecycleBadge — used for filtering. */
const getMeasureLifecycleLabel = (measure) => {
  if (measure.CancelledUtc)                      return "Cancelled";
  if (measure.SignedOffUtc)                       return "Signed Off";
  if (measure.InstallUtc || measure.HandoverUtc) return "Installed";
  return (MEASURE_STATUS_MAP[measure.MeasureStatus] || { label: `Status ${measure.MeasureStatus}` }).label;
};

/**
 * Infer job-level lifecycle label from its measures.
 * Mirrors the backend inferJobLifecycleLabel logic.
 */
const inferJobLifecycleFromMeasures = (measures) => {
  if (!measures || measures.length === 0) return { label: "In Progress", color: "warning" };
  const total     = measures.length;
  const cancelled  = measures.filter((m) => m.CancelledUtc).length;
  const signedOff  = measures.filter((m) => m.SignedOffUtc).length;
  const installed  = measures.filter((m) => m.InstallUtc || m.HandoverUtc).length;
  if (cancelled === total) return { label: "Cancelled",       color: "danger"  };
  if (signedOff === total) return { label: "Signed Off",      color: "success" };
  if (signedOff > 0)       return { label: "Part Signed Off", color: "info"    };
  if (installed > 0)       return { label: "Installed",       color: "primary" };
  return                          { label: "In Progress",     color: "warning" };
};

// PAS Hub job status enum (legacy non-sequential: Lead=7, AssessmentBooked=8 were added later)
const JOB_STATUS_MAP = {
  1:  { label: "Assessment Complete",   color: "success"   },
  2:  { label: "RC Instructed",         color: "info"      },
  3:  { label: "Awaiting Installation", color: "warning"   },
  4:  { label: "Installation Complete", color: "success"   },
  5:  { label: "Awaiting Lodgement",    color: "info"      },
  6:  { label: "Lodged",               color: "success"   },
  7:  { label: "Lead",                 color: "secondary" },
  8:  { label: "Assessment Booked",    color: "info"      },
  9:  { label: "Installation Booked",  color: "primary"   },
  10: { label: "Completed",            color: "success"   },
};

const APPOINTMENT_STATUS_MAP = {
  3: "Booked",
  4: "Completed",
  5: "Cancelled",
};

const APPOINTMENT_TYPE_MAP = {
  1: "Home Survey",
  2: "Other",
  3: "Admin / Follow-up",
};

// PAS Hub project stage status enum (same values as measure status)
const PROJECT_STAGE_STATUS_MAP = {
  1:  { label: "New",                   color: "secondary" },
  2:  { label: "RC Instructed",         color: "info"      },
  3:  { label: "Awaiting Installation", color: "warning"   },
  4:  { label: "Installation Complete", color: "success"   },
  5:  { label: "Awaiting Lodgement",    color: "info"      },
  6:  { label: "Lodged",               color: "success"   },
  9:  { label: "Installation Booked",  color: "primary"   },
  10: { label: "Completed",            color: "success"   },
};

// PAS Hub EcoStatus enum
const ECO_STATUS_MAP = {
  1: { label: "ECO Generating", color: "warning"   },
  2: { label: "ECO New",        color: "info"      },
  3: { label: "ECO Failed",     color: "danger"    },
  4: { label: "ECO Cancelled",  color: "secondary" },
};

const LODGEMENT_STATUS_MAP = {
  0: { label: "No Lodgements",         color: "secondary" },
  1: { label: "Assessment Submitted",  color: "info"      },
  2: { label: "Project Started",       color: "primary"   },
  3: { label: "Lodgement Submitted",   color: "warning"   },
  4: { label: "Project Completed",     color: "success"   },
};

const DWP_STATUS_MAP = {
  0: { label: "DWP Pending",    color: "secondary" },
  1: { label: "DWP Extracted",  color: "info"      },
  2: { label: "DWP Matched",    color: "success"   },
  3: { label: "DWP Unmatched",  color: "danger"    },
  4: { label: "DWP Unverified", color: "warning"   },
  5: { label: "DWP Failed",     color: "danger"    },
  6: { label: "DWP Cancelled",  color: "secondary" },
};

// Temporary fallback lookup maps sourced from live PAS Hub job data.
// Remove when DB lookup tables are synced and joined server-side.
const FUNDING_CATEGORY_FALLBACK_MAP = {
  "bc895f03-ae05-4491-becd-ddd31f88cf1b": "Warm Homes: Social Housing Fund",
  "a9cadbc5-30c1-43aa-9ee6-4f26a81703f2": "Warm Homes: Local Grants",
  "7e7a47c4-ca27-4d34-9872-d514de34c220": "SHDF",
  "d670671b-c5a4-42b8-bb3f-962c61362723": "ECO",
};

const FUNDING_NAME_FALLBACK_MAP = {
  "053490b2-f91a-4819-3fc7-08ddb8eb4ba6": "Wave 3",
  "15e20949-cf49-49a3-f2bd-08ddb8edeeeb": "Phase 1",
  "f098c812-1dba-417e-a104-08db424292b4": "SHDF (Wave 2.1)",
  "bafad21f-bf66-4401-87cd-673c9a595df2": "ECO4",
};

const SCHEME_FALLBACK_MAP = {
  "c932ce3f-5349-4b29-a27b-40e1ec4a64d5": "MTVH WHSHF Wave 3",
  "8e641ce9-989a-4d40-89e4-473edb6159cd": "Essex WH:LG",
  "6a7ddf8b-9f88-4ce1-afb2-c7a875185894": "MTVH SHDF Wave 2.1",
};

// Temporary fallback measure lookup maps sourced from live PAS Hub jobs/measures.
const MEASURE_NAME_FALLBACK_MAP = {
  "c36499d0-bd5f-4fca-8346-0f61b6c6c6e5": "Air Source Heat Pump",
  "2d8c865b-9bd0-4258-ae0d-1c13f74cd572": "Boiler Replacement",
  "237f7643-d9f9-4efb-8c5f-149c92b45980": "Cavity Wall Insulation",
  "01d0895e-45ed-42c6-8d56-45ea5037574a": "Decentralized Mechanical Extract Ventilation",
  "9cffa363-f5d1-4b42-948b-c454ab58c3f4": "Draught-Proofing/Air-Tightness Measures",
  "e0797a22-63ad-415c-93b6-512c5d39cb8a": "Heating And Hot Water Controls",
  "883f2c1a-87c9-4599-949a-7df323ecf0bd": "Loft Insulation (Joists)",
  "3112f524-d2a2-4deb-816a-cc37ac36bd13": "New Central Heating System",
  "20b766e2-b9e9-4c3e-a089-9e8a4f5e1b94": "Other EEMs, including Innovations",
  "5258eb48-db68-4d0b-b34e-739a202fb55f": "Replacement of Electric Storage Heaters",
  "2d5a909b-88c9-467d-91e5-c4167568f3c2": "Room in Roof Insulation (All Elements)",
  "5fac4aae-98a5-4f35-adc2-19978b63e56d": "Single-Room Heat Recovery Ventilators",
  "90472ebf-a3c8-4634-9e61-923bededfb9b": "Solar Photovoltaics",
};

const MEASURE_TYPE_FALLBACK_MAP = {
  "b26818bc-dbad-4cb6-80e5-46b9cd676e7e": "Air Source Heat Pump",
  "949506be-1c14-47ff-970a-22569485e3c3": "Air Source Heat Pump (First Time Central Heating)",
  "a434d337-67cf-4599-bd00-0f1b5836c766": "Air Source Heat Pump (Upgrade)",
  "cea7b074-a5e7-4a5e-9b9f-08de0fb0e897": "Battery Storage",
  "563174bf-8046-4006-ad3f-5dd5d844083b": "Boiler Replacement",
  "d3ddcbd3-8eef-4f24-b70a-1f67099b1d0f": "Cavity Wall Insulation",
  "98898208-5466-4f10-b2bf-376885dcacb8": "Decentralized Mechanical Extract Ventilation",
  "ca524a74-c5ab-4f16-93ab-4af46768dc5e": "Draught-Proofing/Air-Tightness Measures",
  "f4afa0b5-c051-41be-be6e-f185a33805f8": "Electric Storage Heater (Upgrades)",
  "547307cb-b306-47a0-bb7a-27e29b485f37": "Heating and Hot Water Controls",
  "06289671-385d-45dc-9b2d-23beadeb136c": "Loft Insulation (Joists)",
  "e5b29c4c-54bd-4941-a024-a810e0f2617d": "New Central Heating System",
  "18b28153-a830-4cff-943c-5264e83aa690": "Other EEMs, including Innovations",
  "dc328940-47e7-419c-afc4-76b5b79b7738": "Partial Fill Cavity Wall Insulation",
  "9ddad95e-1487-4749-9c4c-71cfdcc295ac": "Single-Room Heat Recovery Ventilators",
  "8dd8207d-e59e-4d5d-9dc7-7e646f3a10c2": "Solar Photovoltaics",
};

const getFundingCategoryLabel = (row) => {
  if (row?.FundingCategory) return row.FundingCategory;
  const id = String(row?.FundingCategoryId || "").toLowerCase();
  return FUNDING_CATEGORY_FALLBACK_MAP[id] || "";
};

const getFundingNameLabel = (row) => {
  if (row?.FundingName) return row.FundingName;
  const id = String(row?.FundingNameId || "").toLowerCase();
  return FUNDING_NAME_FALLBACK_MAP[id] || "";
};

const getSchemeLabel = (row) => {
  if (row?.SchemeName) return row.SchemeName;
  const id = String(row?.PublishedSchemeId || "").toLowerCase();
  return SCHEME_FALLBACK_MAP[id] || "";
};

const getStatusBadge = (statusId, map) => {
  const s = map[statusId] || { label: `Status ${statusId}`, color: "secondary" };
  return (
    <Badge bg={s.color} className="px-2 py-1">
      {s.icon && <FontAwesomeIcon icon={s.icon} className="me-1" />}
      {s.label}
    </Badge>
  );
};

/* ─── FORMAT HELPERS ─────────────────────────────────────────── */

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const camelToTitle = (s) =>
  s
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/* ─── JSON ANSWER FORMATTING ─────────────────────────────────── */

/** Format a single answer value based on question type */
const formatAnswer = (answer, type) => {
  if (answer === null || answer === undefined || answer === "") return null;

  switch (type) {
    case "yes-no-question":
      if (typeof answer === "boolean") return answer ? "Yes" : "No";
      return String(answer);
    case "date-time-question":
      return formatDate(answer);
    case "numeric-question":
      return typeof answer === "number" ? answer.toLocaleString() : String(answer);
    case "list-question":
      if (typeof answer === "object" && answer?.selectedItems) {
        return answer.selectedItems.join(", ") || "—";
      }
      return String(answer);
    case "label-question":
      if (typeof answer === "boolean") return answer ? "Yes" : "No";
      return String(answer);
    case "text-question":
    default:
      return String(answer);
  }
};

/** Parse a JSON field that may be a string or already parsed */
const parseJsonField = (field) => {
  if (!field) return null;
  if (typeof field === "string") {
    try {
      return JSON.parse(field);
    } catch {
      return null;
    }
  }
  return field;
};

/** Extract measures array from varied API payload shapes */
const extractMeasuresArray = (payload) => {
  let source = payload;

  if (typeof source === "string") {
    try {
      source = JSON.parse(source);
    } catch {
      return [];
    }
  }

  if (Array.isArray(source)) return source;
  if (!source || typeof source !== "object") return [];

  const candidates = [
    source.data,
    source.data?.data,
    source.data?.results,
    source.data?.rows,
    source.data?.items,
    source.results,
    source.rows,
    source.items,
    source.value,
    source.measures,
  ];

  const firstArray = candidates.find((c) => Array.isArray(c));
  if (firstArray) return firstArray;

  // Some APIs return a single measure object instead of a list.
  if (source.Id || source.MeasureId || source.JobId || source.JobUId) {
    return [source];
  }

  return [];
};

/** Section label mapping for DefaultMeasureDetails sections */
const SECTION_LABELS = {
  125153: "Measure Details",
  125324: "Product & Guarantee",
  125325: "ECO & Compliance",
};

/* ─── GROUPING UTILITY ───────────────────────────────────────── */

/** Derive the best display name for a measure from available data */
const getMeasureDisplayName = (measure, simple) => {
  const nameLookup = MEASURE_NAME_FALLBACK_MAP[String(measure?.PasMeasureNameLookupId || "").toLowerCase()] || null;
  const typeLookup = MEASURE_TYPE_FALLBACK_MAP[String(measure?.PasMeasureTypeLookupId || "").toLowerCase()] || null;

  const candidates = [
    measure.PasMeasureName,
    simple?.pasMeasureName,
    simple?.workDescription,
    nameLookup,
    simple?.pasMeasureType,
    typeLookup,
    measure.MeasureName,
    measure.EcoMeasureName,
    measure.PasMeasureType,
    measure.MeasureReference,
  ];

  const firstFilled = candidates.find(
    (v) => typeof v === "string" && v.trim().length > 0
  );

  if (firstFilled) return firstFilled;

  if (measure?.PasMeasureTypeLookupId) {
    return `PAS Measure ${String(measure.PasMeasureTypeLookupId).slice(0, 8)}`;
  }

  return null;
};

/** Check whether a measure has any meaningful detail data filled in */
const hasMeasureDetails = (measure) => {
  const simple =
    typeof measure.SimpleMeasureDetails === "string"
      ? (() => { try { return JSON.parse(measure.SimpleMeasureDetails); } catch { return null; } })()
      : measure.SimpleMeasureDetails;
  return !!(
    getMeasureDisplayName(measure, simple) ||
    measure.InstallUtc ||
    measure.TrustMarkUmr ||
    measure.MeasureReference ||
    measure.InstallationCost ||
    (simple && Object.keys(simple).length > 0)
  );
};

const groupByProperty = (measures) => {
  const map = {};

  const getRowTimestamp = (row) => {
    const updated = row?.UpdatedUtc ? new Date(row.UpdatedUtc).getTime() : NaN;
    if (!Number.isNaN(updated) && updated > 0) return updated;
    const created = row?.CreatedUtc ? new Date(row.CreatedUtc).getTime() : NaN;
    if (!Number.isNaN(created) && created > 0) return created;
    return 0;
  };

  measures.forEach((m) => {
    const key = m.SingleLineAddress || m.JobUId || "Unknown Property";
    if (!map[key]) {
      map[key] = {
        address: m.SingleLineAddress,
        postcode: m.AddressPostcode,
        jobs: {},
      };
    }
    const jobKey = m.JobId || m.JobUId || "unknown-job";
    if (!map[key].jobs[jobKey]) {
      map[key].jobs[jobKey] = {
        jobId: m.JobId,
        jobUId: m.JobUId,
        externalCrmId: m.ExternalCrmId,
        jobStatusId: m.JobStatusId,
        lodgementStatus: m.LodgementStatus,
        jobTypeId: m.JobTypeId,
        companyName: m.CompanyName,
        companyId: m.CompanyId,
        createdByName: m.CreatedByName,
        jobCreatedUtc: m.JobCreatedUtc || (m.IsJobOnly ? m.CreatedUtc : null),
        primaryResidentName: m.PrimaryResidentName,
        residentNames: m.ResidentNames,
        assessmentRef: m.AssessmentRef,
        dwpStatus: m.DwpStatus,
        isOnHold: m.IsOnHold,
        jobCancelledUtc: m.CancelledUtc,
        annualFuelSaving: m.AnnualFuelSaving,
        potentialEnergyRating: m.PotentialEnergyRating,
        currentEnergyRating: m.CurrentEnergyRating,
        projectStageCount: m.ProjectStageCount,
        pausedStageCount: m.PausedStageCount,
        cancelledStageCount: m.CancelledStageCount,
        surveyHasDampMouldIssue: m.SurveyHasDampMouldIssue,
        surveyHasHHSRSIssue: m.SurveyHasHHSRSIssue,
        surveyCurrentSAP: m.SurveyCurrentSAP,
        surveyPotentialSAP: m.SurveyPotentialSAP,
        surveyWallType: m.SurveyWallType,
        surveyBuiltForm: m.SurveyBuiltForm,
        customerContribution: m.CustomerContribution,
        invoiceTotal: m.InvoiceTotal,
        _statusAsOfUtc: getRowTimestamp(m),
        measures: [],
      };
    } else {
      const existing = map[key].jobs[jobKey];
      const candidateTs = getRowTimestamp(m);
      const currentTs = existing._statusAsOfUtc || 0;

      // Job-level fields in MeasureSummary can vary per row over time.
      // Keep the values from the latest snapshot only.
      if (candidateTs >= currentTs) {
        existing.externalCrmId = m.ExternalCrmId;
        existing.jobStatusId = m.JobStatusId;
        existing.lodgementStatus = m.LodgementStatus;
        existing.jobTypeId = m.JobTypeId;
        existing.companyName = m.CompanyName;
        existing.companyId = m.CompanyId;
        existing.createdByName = m.CreatedByName;
        existing.jobCreatedUtc = m.JobCreatedUtc || (m.IsJobOnly ? m.CreatedUtc : null) || existing.jobCreatedUtc;
        existing.primaryResidentName = m.PrimaryResidentName || existing.primaryResidentName;
        existing.residentNames = m.ResidentNames || existing.residentNames;
        existing.assessmentRef = m.AssessmentRef;
        existing.dwpStatus = m.DwpStatus;
        existing.isOnHold = m.IsOnHold;
        existing.jobCancelledUtc = m.CancelledUtc;
        existing.annualFuelSaving = m.AnnualFuelSaving;
        existing.potentialEnergyRating = m.PotentialEnergyRating;
        existing.currentEnergyRating = m.CurrentEnergyRating;
        existing.projectStageCount = m.ProjectStageCount;
        existing.pausedStageCount = m.PausedStageCount;
        existing.cancelledStageCount = m.CancelledStageCount;
        existing.surveyHasDampMouldIssue = m.SurveyHasDampMouldIssue;
        existing.surveyHasHHSRSIssue = m.SurveyHasHHSRSIssue;
        existing.surveyCurrentSAP = m.SurveyCurrentSAP;
        existing.surveyPotentialSAP = m.SurveyPotentialSAP;
        existing.surveyWallType = m.SurveyWallType;
        existing.surveyBuiltForm = m.SurveyBuiltForm;
        existing.customerContribution = m.CustomerContribution;
        existing.invoiceTotal = m.InvoiceTotal;
        existing._statusAsOfUtc = candidateTs;
      }
    }
    map[key].jobs[jobKey].measures.push(m);
  });

  Object.values(map).forEach((property) => {
    Object.values(property.jobs).forEach((job) => {
      delete job._statusAsOfUtc;
      job.jobLifecycleLabel = inferJobLifecycleFromMeasures(job.measures);
    });
  });

  return map;
};


/* ─── SURVEY SECTION RENDERER (DefaultMeasureDetails) ────────── */

const SurveySection = ({ section, showHidden }) => {
  const sectionLabel = SECTION_LABELS[section.id] || `Section ${section.id}`;
  const answers = (section.answers || []).filter(
    (q) => showHidden || q.visible
  );

  if (answers.length === 0) return null;

  const visibleCount = section.answers.filter((q) => q.visible).length;
  const hiddenCount = section.answers.filter((q) => !q.visible).length;

  return (
    <Card className="survey-section-card mb-3 border-0 shadow-sm">
      <Card.Header className="bg-white border-bottom py-2 px-3">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-semibold text-primary">
            <FontAwesomeIcon icon={faClipboardList} className="me-2" />
            {sectionLabel}
          </h6>
          <div className="d-flex gap-2">
            <Badge bg="primary" pill className="px-2">
              {visibleCount} visible
            </Badge>
            {hiddenCount > 0 && (
              <Badge bg="secondary" pill className="px-2">
                {hiddenCount} hidden
              </Badge>
            )}
          </div>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {answers.map((q) => {
          const formatted = formatAnswer(q.answer, q.type);
          if (formatted === null && !showHidden) return null;
          const label = q.shortQuestionText ? camelToTitle(q.shortQuestionText) : `Q ${q.id}`;
          return (
            <div
              key={q.id}
              className={`survey-answer-row d-flex align-items-start px-3 py-2 border-bottom border-light ${
                !q.visible ? "bg-light text-muted" : ""
              }`}
            >
              <div className="survey-answer-label text-muted small" style={{ minWidth: "40%", maxWidth: "40%" }}>
                {label}
                {!q.visible && (
                  <FontAwesomeIcon icon={faEyeSlash} className="ms-1 text-secondary" style={{ fontSize: "0.7rem" }} />
                )}
              </div>
              <div className="survey-answer-value flex-fill fw-semibold small">
                {formatted || <span className="text-muted fst-italic">Empty</span>}
              </div>
              <Badge
                bg="light"
                text="muted"
                className="border ms-2 survey-type-badge"
                style={{ fontSize: "0.65rem", whiteSpace: "nowrap" }}
              >
                {q.type?.replace("-question", "")}
              </Badge>
            </div>
          );
        })}
      </Card.Body>
    </Card>
  );
};

/* ─── RAW ANSWERS TABLE ──────────────────────────────────────── */

const RawAnswersTable = ({ rawData, showHidden }) => {
  if (!rawData || rawData.length === 0) return <p className="text-muted small">No raw data available.</p>;

  const items = showHidden ? rawData : rawData.filter((q) => q.visible);

  return (
    <div className="raw-answers-table">
      <div className="d-flex justify-content-between mb-2">
        <span className="text-muted small">
          {items.length} of {rawData.length} answers
        </span>
      </div>
      {items.map((q, idx) => {
        const formatted = formatAnswer(q.answer, q.type);
        return (
          <div
            key={q.id || idx}
            className={`d-flex align-items-center px-3 py-2 border-bottom border-light ${
              !q.visible ? "bg-light" : ""
            }`}
          >
            <div className="font-monospace text-muted small me-3" style={{ minWidth: 100 }}>
              #{q.id}
            </div>
            <div className="flex-fill small fw-semibold">
              {formatted || <span className="text-muted fst-italic">Empty</span>}
            </div>
            <Badge bg="light" text="muted" className="border ms-2" style={{ fontSize: "0.65rem" }}>
              {q.type?.replace("-question", "")}
            </Badge>
            {!q.visible && (
              <FontAwesomeIcon icon={faEyeSlash} className="ms-2 text-secondary" style={{ fontSize: "0.7rem" }} />
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── STAGE TYPE LABELS ──────────────────────────────────────── */
const STAGE_TYPE_LABELS = {
  1: "Pre-Installation",
  2: "Installation",
  3: "Post-Installation",
  4: "Handover",
};

const STAGE_STATUS_LABELS = {
  1: { label: "Pending", color: "secondary", icon: faClock },
  2: { label: "Approved", color: "success", icon: faCheckCircle },
  3: { label: "Rejected", color: "danger", icon: faTimesCircle },
};

const EVIDENCE_SOURCE_LABELS = {
  1: "System Generated",
  2: "User Upload",
};

/* ─── MEASURE DETAIL MODAL ───────────────────────────────────── */

const MeasureDetailModal = ({ show, onHide, measure, instance }) => {
  const [activeTab, setActiveTab] = useState("summary");
  const [showHidden, setShowHidden] = useState(false);
  const [stages, setStages] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [stagesLoaded, setStagesLoaded] = useState(false);
  const [evidenceLoaded, setEvidenceLoaded] = useState(false);

  // Load stages/evidence when their tab is selected
  useEffect(() => {
    if (!measure || !show) {
      setStagesLoaded(false);
      setEvidenceLoaded(false);
      setStages([]);
      setEvidence([]);
      return;
    }

    const loadStages = async () => {
      if (stagesLoaded || stagesLoading) return;
      setStagesLoading(true);
      try {
        const res = await measuresApiService.getMeasureStages(instance, measure.Id);
        setStages(res.data || []);
      } catch {
        setStages([]);
      } finally {
        setStagesLoading(false);
        setStagesLoaded(true);
      }
    };

    const loadEvidence = async () => {
      if (evidenceLoaded || evidenceLoading) return;
      setEvidenceLoading(true);
      try {
        const res = await measuresApiService.getMeasureEvidence(instance, measure.Id);
        setEvidence(res.data || []);
      } catch {
        setEvidence([]);
      } finally {
        setEvidenceLoading(false);
        setEvidenceLoaded(true);
      }
    };

    if (activeTab === "stages") loadStages();
    if (activeTab === "evidence") loadEvidence();
  }, [activeTab, measure, show, instance, stagesLoaded, evidenceLoaded, stagesLoading, evidenceLoading]);

  if (!measure) return null;

  const simple = parseJsonField(measure.SimpleMeasureDetails);
  const defaultDetails = parseJsonField(measure.DefaultMeasureDetails);
  const rawDetails = parseJsonField(measure.RawMeasureDetails);

  const sections = defaultDetails?.answers?.filter((a) => a.type === "standard-section") || [];
  const hasDefaultDetails = sections.length > 0;
  const hasRawDetails = Array.isArray(rawDetails) && rawDetails.length > 0;

  const importantKeys = [
    "workDescription",
    "pasMeasureName",
    "pasMeasureType",
    "trustMarkMeasureType",
    "productManufacturer",
    "productModel",
    "productUnits",
    "productValue",
    "measureGuarantee",
    "guaranteeStartDate",
    "wallGuaranteeCertificateNumber",
    "mcsCertificateNumber",
    "operativeCertificationReference",
    "ecoScheme",
    "subcontractorYN",
    "subcontractorName",
    "has100percentMeasureInstalled",
  ];

  const otherKeys = simple
    ? Object.keys(simple).filter(
        (k) => !importantKeys.includes(k) && simple[k] !== "" && simple[k] !== null && simple[k] !== undefined
      )
    : [];

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="measure-detail-header border-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faWrench} />
          <span>{measure.PasMeasureName || simple?.pasMeasureName || simple?.workDescription || "Measure Details"}</span>
          {getMeasureLifecycleBadge(measure)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        {/* Key dates + stats strip */}
        <div className="p-4 bg-light border-bottom">
          <Row className="mb-3">
            <Col sm={4}>
              <div className="text-muted small">Install Date</div>
              <div className="fw-semibold">{formatDate(measure.InstallUtc)}</div>
            </Col>
            <Col sm={4}>
              <div className="text-muted small">Handover Date</div>
              <div className="fw-semibold">{formatDate(measure.HandoverUtc)}</div>
            </Col>
            <Col sm={4}>
              <div className="text-muted small">Signed Off</div>
              <div className="fw-semibold">{formatDate(measure.SignedOffUtc)}</div>
            </Col>
          </Row>
          <Row className="g-3">
            {[
              { label: "Evidence", value: measure.EvidenceCount ?? 0 },
              { label: "Stages", value: measure.StageCount ?? 0 },
              { label: "Site Visits", value: measure.SiteVisitCount ?? 0 },
              {
                label: "Cost",
                value: measure.InstallationCost
                  ? `£${Number(measure.InstallationCost).toLocaleString()}`
                  : "—",
              },
            ].map((s) => (
              <Col key={s.label} xs={6} sm={3}>
                <Card className="text-center border-0 bg-white h-100 shadow-sm">
                  <Card.Body className="py-2">
                    <div className="text-muted small">{s.label}</div>
                    <div className="fs-5 fw-bold text-primary">{s.value}</div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </div>

        {/* Tabs: Summary | Survey Details | Raw Answers */}
        <div className="p-4">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3 measure-detail-tabs"
          >
            {/* ─── TAB 1: SUMMARY ─── */}
            <Tab
              eventKey="summary"
              title={
                <span>
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  Summary
                </span>
              }
            >
              {simple && (
                <>
                  <div className="measure-detail-grid mb-3">
                    {importantKeys.map((k) => {
                      const val = simple[k];
                      if (val === "" || val === null || val === undefined) return null;
                      return (
                        <Row key={k} className="py-2 border-bottom border-light">
                          <Col sm={5} className="text-muted small">
                            {camelToTitle(k)}
                          </Col>
                          <Col sm={7} className="fw-semibold">
                            {typeof val === "boolean" ? (val ? "Yes" : "No") : String(val)}
                          </Col>
                        </Row>
                      );
                    })}
                  </div>

                  {otherKeys.length > 0 && (
                    <Accordion className="mt-3">
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          <span className="small text-muted">
                            Additional Details ({otherKeys.length} fields)
                          </span>
                        </Accordion.Header>
                        <Accordion.Body className="p-2">
                          {otherKeys.map((k) => {
                            const val = simple[k];
                            return (
                              <Row key={k} className="py-1 border-bottom border-light">
                                <Col sm={5} className="text-muted small">
                                  {camelToTitle(k)}
                                </Col>
                                <Col sm={7} className="small">
                                  {typeof val === "boolean" ? (val ? "Yes" : "No") : String(val)}
                                </Col>
                              </Row>
                            );
                          })}
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  )}
                </>
              )}

              {/* References & People */}
              <div className="mt-4 p-3 bg-light rounded">
                <h6 className="text-muted small mb-2">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                  References
                </h6>
                <Row>
                  <Col sm={6} className="small mb-1">
                    <span className="text-muted">Measure ID:</span>{" "}
                    <span className="font-monospace">{measure.Id?.substring(0, 8)}…</span>
                  </Col>
                  <Col sm={6} className="small mb-1">
                    <span className="text-muted">Project Stage:</span>{" "}
                    {measure.ProjectStageName || "—"}
                    {measure.ProjectStageStatus != null && (
                      <span className="ms-1">
                        {getStatusBadge(measure.ProjectStageStatus, PROJECT_STAGE_STATUS_MAP)}
                      </span>
                    )}
                  </Col>
                </Row>
                {measure.TrustMarkUmr && (
                  <Row className="mt-1">
                    <Col sm={6} className="small">
                      <span className="text-muted">TrustMark UMR:</span>{" "}
                      <span className="font-monospace">{measure.TrustMarkUmr}</span>
                    </Col>
                  </Row>
                )}
                {measure.TrustMarkMeasureId && (
                  <Row className="mt-1">
                    <Col sm={6} className="small">
                      <span className="text-muted">TrustMark Measure ID:</span>{" "}
                      <span className="font-monospace">{measure.TrustMarkMeasureId}</span>
                    </Col>
                  </Row>
                )}
                {measure.MeasureReference && (
                  <Row className="mt-1">
                    <Col className="small">
                      <span className="text-muted">Measure Ref:</span> {measure.MeasureReference}
                    </Col>
                  </Row>
                )}
                {measure.SubInstallerTrustMarkLicenceNumber && (
                  <Row className="mt-1">
                    <Col className="small">
                      <span className="text-muted">Sub-Installer TrustMark Licence:</span>{" "}
                      <span className="font-monospace">{measure.SubInstallerTrustMarkLicenceNumber}</span>
                    </Col>
                  </Row>
                )}
                {measure.LotName && (
                  <Row className="mt-1">
                    <Col className="small">
                      <span className="text-muted">Lot:</span> {measure.LotName}
                    </Col>
                  </Row>
                )}
              </div>

              {/* People & Audit */}
              {(measure.CreatedByName || measure.UpdatedByName || measure.SignedOffByName || measure.CancelledByName) && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="text-muted small mb-2">
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    People & Audit
                  </h6>
                  <Row>
                    {measure.CreatedByName && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Created By:</span> {measure.CreatedByName}
                      </Col>
                    )}
                    {measure.UpdatedByName && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Updated By:</span> {measure.UpdatedByName}
                      </Col>
                    )}
                    {measure.SignedOffByName && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Signed Off By:</span> {measure.SignedOffByName}
                      </Col>
                    )}
                    {measure.CancelledByName && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Cancelled By:</span> {measure.CancelledByName}
                      </Col>
                    )}
                  </Row>
                  <Row className="mt-1">
                    {measure.CreatedUtc && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Created:</span> {formatDateTime(measure.CreatedUtc)}
                      </Col>
                    )}
                    {measure.UpdatedUtc && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Updated:</span> {formatDateTime(measure.UpdatedUtc)}
                      </Col>
                    )}
                    {measure.CancelledUtc && (
                      <Col sm={6} className="small mb-1">
                        <span className="text-muted">Cancelled:</span> {formatDateTime(measure.CancelledUtc)}
                      </Col>
                    )}
                  </Row>
                </div>
              )}
            </Tab>

            {/* ─── TAB 2: SURVEY DETAILS (DefaultMeasureDetails) ─── */}
            <Tab
              eventKey="survey"
              title={
                <span>
                  <FontAwesomeIcon icon={faListAlt} className="me-1" />
                  Survey Details
                  {hasDefaultDetails && (
                    <Badge bg="primary" pill className="ms-1" style={{ fontSize: "0.65rem" }}>
                      {sections.length}
                    </Badge>
                  )}
                </span>
              }
            >
              {hasDefaultDetails ? (
                <>
                  {/* Show/hide hidden toggle */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted small mb-0">
                      Structured survey data from the measure form. Sections group related questions.
                    </p>
                    <Button
                      variant={showHidden ? "outline-warning" : "outline-secondary"}
                      size="sm"
                      onClick={() => setShowHidden(!showHidden)}
                    >
                      <FontAwesomeIcon
                        icon={showHidden ? faToggleOn : faToggleOff}
                        className="me-1"
                      />
                      {showHidden ? "Showing hidden" : "Show hidden"}
                    </Button>
                  </div>
                  {sections.map((section) => (
                    <SurveySection
                      key={section.id}
                      section={section}
                      showHidden={showHidden}
                    />
                  ))}
                </>
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faQuestionCircle} size="2x" className="text-muted mb-2" />
                  <p className="text-muted small">No structured survey data available for this measure.</p>
                </div>
              )}
            </Tab>

            {/* ─── TAB 3: RAW ANSWERS (RawMeasureDetails) ─── */}
            <Tab
              eventKey="raw"
              title={
                <span>
                  <FontAwesomeIcon icon={faDatabase} className="me-1" />
                  Raw Answers
                  {hasRawDetails && (
                    <Badge bg="secondary" pill className="ms-1" style={{ fontSize: "0.65rem" }}>
                      {rawDetails.length}
                    </Badge>
                  )}
                </span>
              }
            >
              {hasRawDetails ? (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <p className="text-muted small mb-0">
                      Flat list of all question/answer pairs as stored in the database.
                    </p>
                    <Button
                      variant={showHidden ? "outline-warning" : "outline-secondary"}
                      size="sm"
                      onClick={() => setShowHidden(!showHidden)}
                    >
                      <FontAwesomeIcon
                        icon={showHidden ? faToggleOn : faToggleOff}
                        className="me-1"
                      />
                      {showHidden ? "Showing hidden" : "Show hidden"}
                    </Button>
                  </div>
                  <RawAnswersTable rawData={rawDetails} showHidden={showHidden} />
                </>
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faDatabase} size="2x" className="text-muted mb-2" />
                  <p className="text-muted small">No raw answer data available for this measure.</p>
                </div>
              )}
            </Tab>

            {/* ─── TAB 4: STAGES ─── */}
            <Tab
              eventKey="stages"
              title={
                <span>
                  <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                  Stages
                  {(measure.StageCount ?? 0) > 0 && (
                    <Badge bg="info" pill className="ms-1" style={{ fontSize: "0.65rem" }}>
                      {measure.StageCount}
                    </Badge>
                  )}
                </span>
              }
            >
              {stagesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <p className="text-muted small mt-2">Loading stages…</p>
                </div>
              ) : stages.length > 0 ? (
                <div className="stages-timeline">
                  {stages.map((stage, idx) => {
                    const stageInfo = STAGE_STATUS_LABELS[stage.MeasureStageStatus] || STAGE_STATUS_LABELS[1];
                    const typeName = STAGE_TYPE_LABELS[stage.MeasureStageType] || `Stage ${stage.MeasureStageType}`;
                    const isLast = idx === stages.length - 1;
                    return (
                      <div key={stage.MeasureStageId} className="d-flex mb-0">
                        {/* Timeline line + dot */}
                        <div className="d-flex flex-column align-items-center me-3" style={{ width: 30 }}>
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 28,
                              height: 28,
                              backgroundColor:
                                stageInfo.color === "success" ? "#d4edda"
                                : stageInfo.color === "danger" ? "#f8d7da"
                                : "#e2e3e5",
                              flexShrink: 0,
                            }}
                          >
                            <FontAwesomeIcon
                              icon={stageInfo.icon}
                              style={{
                                fontSize: "0.75rem",
                                color:
                                  stageInfo.color === "success" ? "#155724"
                                  : stageInfo.color === "danger" ? "#721c24"
                                  : "#6c757d",
                              }}
                            />
                          </div>
                          {!isLast && (
                            <div style={{
                              width: 2,
                              flex: 1,
                              minHeight: 20,
                              backgroundColor:
                                stageInfo.color === "success" ? "#28a745" : "#dee2e6",
                            }} />
                          )}
                        </div>
                        {/* Stage content */}
                        <Card className="flex-fill mb-3 border-0 shadow-sm">
                          <Card.Body className="py-2 px-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <div className="fw-semibold">{typeName}</div>
                                <div className="text-muted small">
                                  {stage.MeasureName || ""}
                                </div>
                              </div>
                              <Badge bg={stageInfo.color} className="ms-2">
                                {stageInfo.label}
                              </Badge>
                            </div>
                            {(stage.ApprovedUtc || stage.RejectedUtc) && (
                              <div className="mt-2 pt-2 border-top border-light small">
                                {stage.ApprovedUtc && (
                                  <div className="text-success">
                                    <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                                    Approved {formatDateTime(stage.ApprovedUtc)}
                                    {stage.ApprovedByName && (
                                      <span className="text-muted ms-1">by {stage.ApprovedByName}</span>
                                    )}
                                  </div>
                                )}
                                {stage.RejectedUtc && (
                                  <div className="text-danger">
                                    <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                                    Rejected {formatDateTime(stage.RejectedUtc)}
                                    {stage.RejectedByName && (
                                      <span className="text-muted ms-1">by {stage.RejectedByName}</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                            <div className="mt-1 text-muted" style={{ fontSize: "0.7rem" }}>
                              Created {formatDateTime(stage.CreatedUtc)}
                              {stage.CreatedByName && ` by ${stage.CreatedByName}`}
                              {stage.UpdatedByName && stage.UpdatedByName !== stage.CreatedByName && (
                                <span> · Updated by {stage.UpdatedByName}</span>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faClipboardList} size="2x" className="text-muted mb-2" />
                  <p className="text-muted small">No stage data available for this measure.</p>
                </div>
              )}
            </Tab>

            {/* ─── TAB 5: EVIDENCE ─── */}
            <Tab
              eventKey="evidence"
              title={
                <span>
                  <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                  Evidence
                  {(measure.EvidenceCount ?? 0) > 0 && (
                    <Badge bg="warning" text="dark" pill className="ms-1" style={{ fontSize: "0.65rem" }}>
                      {measure.EvidenceCount}
                    </Badge>
                  )}
                </span>
              }
            >
              {evidenceLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <p className="text-muted small mt-2">Loading evidence…</p>
                </div>
              ) : evidence.length > 0 ? (
                <div className="evidence-list">
                  {evidence.map((ev) => {
                    const ext = (ev.FileName || "").split(".").pop().toLowerCase();
                    const isPdf = ext === "pdf";
                    const isXml = ext === "xml";
                    const isImage = ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(ext);

                    return (
                      <Card key={ev.MeasureEvidenceId} className="mb-2 border-0 shadow-sm">
                        <Card.Body className="py-2 px-3">
                          <div className="d-flex align-items-start gap-2">
                            <div
                              className="d-flex align-items-center justify-content-center rounded"
                              style={{
                                width: 36,
                                height: 36,
                                backgroundColor: isPdf ? "#fce4ec" : isXml ? "#e8f5e9" : isImage ? "#e3f2fd" : "#f5f5f5",
                                flexShrink: 0,
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faFileAlt}
                                style={{
                                  color: isPdf ? "#c62828" : isXml ? "#2e7d32" : isImage ? "#1565c0" : "#757575",
                                  fontSize: "0.9rem",
                                }}
                              />
                            </div>
                            <div className="flex-fill" style={{ minWidth: 0 }}>
                              <div className="fw-semibold small text-truncate" title={ev.FileName}>
                                {ev.FileName || "Unnamed file"}
                              </div>
                              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                                {EVIDENCE_SOURCE_LABELS[ev.EvidenceSource] || `Source ${ev.EvidenceSource}`}
                                {ev.CreatedByName && <span> · {ev.CreatedByName}</span>}
                                {ev.CreatedUtc && <span> · {formatDateTime(ev.CreatedUtc)}</span>}
                              </div>
                            </div>
                            <Badge
                              bg="light"
                              text="dark"
                              className="border"
                              style={{ fontSize: "0.65rem" }}
                            >
                              .{ext}
                            </Badge>
                          </div>
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faFileAlt} size="2x" className="text-muted mb-2" />
                  <p className="text-muted small">No evidence items available for this measure.</p>
                </div>
              )}
            </Tab>
          </Tabs>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-0">
        <Button variant="outline-secondary" size="sm" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

/* ─── MEASURE CARD ───────────────────────────────────────────── */

const MeasureCard = ({ measure, onViewDetails }) => {
  const simple =
    typeof measure.SimpleMeasureDetails === "string"
      ? JSON.parse(measure.SimpleMeasureDetails)
      : measure.SimpleMeasureDetails;

  const statusInfo = MEASURE_STATUS_MAP[measure.MeasureStatus] || {
    label: "Unknown",
    color: "secondary",
    icon: faFileAlt,
  };

  const displayName = getMeasureDisplayName(measure, simple);
  // Only show compact "pending" card for truly empty early-stage measures:
  // no name, no detail data, no install dates, no evidence, and status ≤ 1
  const isPending = !displayName && !simple &&
    !measure.InstallUtc && !measure.HandoverUtc &&
    (measure.EvidenceCount ?? 0) === 0 &&
    measure.MeasureStatus <= 1;

  /* ── Compact pending card for measures with no details yet ── */
  if (isPending) {
    return (
      <Card className="measure-card mb-3 border-0 shadow-sm" style={{ opacity: 0.8 }}>
        <Card.Body className="p-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <div
                className="measure-icon-circle"
                style={{ backgroundColor: "#e2e3e5" }}
              >
                <FontAwesomeIcon icon={faClock} style={{ color: "#6c757d" }} />
              </div>
              <div>
                <h6 className="mb-0 fw-semibold text-muted">
                  Measure Pending
                </h6>
                <span className="text-muted small">
                  {measure.ProjectStageName || ""}
                  {measure.ProjectStageStatus != null && (
                    <span className="ms-1">{getStatusBadge(measure.ProjectStageStatus, PROJECT_STAGE_STATUS_MAP)}</span>
                  )}
                </span>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              {getMeasureLifecycleBadge(measure)}
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-light">
            <div className="d-flex gap-3">
              {measure.IsManual === 1 && (
                <span className="small text-muted">
                  <Badge bg="outline-secondary" className="border border-secondary text-secondary" style={{ fontSize: "0.7rem" }}>Manual</Badge>
                </span>
              )}
              <span className="small text-muted">
                Created {formatDate(measure.CreatedUtc)}
              </span>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={() => onViewDetails(measure)}>
              <FontAwesomeIcon icon={faEye} className="me-1" />
              Details
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  }

  /* ── Standard card for measures with data ── */

  return (
    <Card className="measure-card mb-3 border-0 shadow-sm">
      <Card.Body className="p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2">
            <div
              className="measure-icon-circle"
              style={{
                backgroundColor:
                  statusInfo.color === "success"
                    ? "#d4edda"
                    : statusInfo.color === "primary"
                    ? "#cce5ff"
                    : statusInfo.color === "danger"
                    ? "#f8d7da"
                    : "#e2e3e5",
              }}
            >
              <FontAwesomeIcon
                icon={faSolarPanel}
                style={{
                  color:
                    statusInfo.color === "success"
                      ? "#155724"
                      : statusInfo.color === "primary"
                      ? "#004085"
                      : statusInfo.color === "danger"
                      ? "#721c24"
                      : "#383d41",
                }}
              />
            </div>
            <div>
              <h6 className="mb-0 fw-semibold">
                {displayName || `Measure — ${statusInfo.label}`}
              </h6>
              <span className="text-muted small">
                {simple?.pasMeasureType || measure.PasMeasureType || ""}
              </span>
            </div>
          </div>
          {getMeasureLifecycleBadge(measure)}
        </div>

        {/* Key info pills */}
        <div className="d-flex flex-wrap gap-2 mb-2">
          {measure.InstallUtc && (
            <span className="measure-pill">
              <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-muted" />
              Installed {formatDate(measure.InstallUtc)}
            </span>
          )}
          {simple?.productManufacturer && (
            <span className="measure-pill">
              <FontAwesomeIcon icon={faBuilding} className="me-1 text-muted" />
              {simple.productManufacturer}
            </span>
          )}
          {simple?.productModel && (
            <span className="measure-pill">{simple.productModel}</span>
          )}
          {measure.InstallationCost && (
            <span className="measure-pill">
              <FontAwesomeIcon icon={faPoundSign} className="me-1 text-muted" />£
              {Number(measure.InstallationCost).toLocaleString()}
            </span>
          )}
          {simple?.ecoScheme && simple.ecoScheme !== "None" && (
            <Badge bg="outline-info" className="measure-pill border border-info text-info">
              {simple.ecoScheme}
            </Badge>
          )}
          {measure.LotName && (
            <span className="measure-pill">
              <FontAwesomeIcon icon={faTag} className="me-1 text-muted" />
              {measure.LotName}
            </span>
          )}
          {measure.DesignCompleted === true || measure.DesignCompleted === 1 ? (
            <OverlayTrigger overlay={<Tooltip>Design completed</Tooltip>}>
              <Badge bg="success" style={{ fontSize: "0.7rem" }}>Design ✓</Badge>
            </OverlayTrigger>
          ) : null}
          {measure.EcoStatus && ECO_STATUS_MAP[measure.EcoStatus] && (
            <Badge bg={ECO_STATUS_MAP[measure.EcoStatus].color} style={{ fontSize: "0.7rem" }}>
              {ECO_STATUS_MAP[measure.EcoStatus].label}
            </Badge>
          )}
        </div>

        {/* Mini stats */}
        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-light">
          <div className="d-flex gap-3">
            <OverlayTrigger overlay={<Tooltip>Evidence items</Tooltip>}>
              <span className="small text-muted">
                <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                {measure.EvidenceCount ?? 0}
              </span>
            </OverlayTrigger>
            <OverlayTrigger overlay={<Tooltip>Stages</Tooltip>}>
              <span className="small text-muted">
                <FontAwesomeIcon icon={faClipboardList} className="me-1" />
                {measure.StageCount ?? 0}
              </span>
            </OverlayTrigger>
            {measure.SiteVisitCount > 0 && (
              <OverlayTrigger overlay={<Tooltip>Site visits</Tooltip>}>
                <span className="small text-muted">
                  <FontAwesomeIcon icon={faHome} className="me-1" />
                  {measure.SiteVisitCount}
                </span>
              </OverlayTrigger>
            )}
          </div>
          <Button variant="outline-primary" size="sm" onClick={() => onViewDetails(measure)}>
            <FontAwesomeIcon icon={faEye} className="me-1" />
            Details
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ─── PIPELINE STEP INDICATOR ────────────────────────────────── */

const PIPELINE_STEPS = [
  { key: "shortlisted", label: "Shortlisted", icon: faLightbulb,   color: "#ff9800" },
  { key: "submitted",   label: "Submitted",   icon: faPaperPlane,  color: "#0288d1" },
  { key: "inprogress",  label: "In Progress", icon: faWrench,      color: "#1976d2" },
  { key: "installed",   label: "Installed",   icon: faCheckCircle, color: "#388e3c" },
  { key: "signedoff",   label: "Signed Off",  icon: faCertificate, color: "#2e7d32" },
];

// Pipeline steps (date-first): 0=shortlisted, 1=submitted, 2=inProgress, 3=installed, 4=signedOff, -1=cancelled
const getPipelineStep = (measure) => {
  if (!measure) return 0;
  if (measure.IsRecommendation) return 0;
  if (measure.CancelledUtc) return -1;
  if (measure.SignedOffUtc) return 4;
  if (measure.InstallUtc || measure.HandoverUtc) return 3;
  if (Number(measure.StageCount) > 0) return 2;
  return 1; // actioned but no stages yet = Submitted
};

const PipelineStepIndicator = ({ currentStep }) => (
  <div className="d-flex align-items-center gap-0 pipeline-steps">
    {PIPELINE_STEPS.map((step, idx) => {
      const isActive = idx <= currentStep;
      const isCurrent = idx === currentStep;
      return (
        <React.Fragment key={step.key}>
          {idx > 0 && (
            <div
              className="pipeline-connector"
              style={{ backgroundColor: isActive ? step.color : "#dee2e6" }}
            />
          )}
          <OverlayTrigger overlay={<Tooltip>{step.label}</Tooltip>}>
            <div
              className={`pipeline-dot ${isCurrent ? "pipeline-dot-current" : ""}`}
              style={{
                backgroundColor: isActive ? step.color : "#f5f5f5",
                borderColor: isActive ? step.color : "#dee2e6",
              }}
            >
              <FontAwesomeIcon
                icon={step.icon}
                style={{
                  color: isActive ? "#fff" : "#bdbdbd",
                  fontSize: "0.6rem",
                }}
              />
            </div>
          </OverlayTrigger>
        </React.Fragment>
      );
    })}
  </div>
);

/* ─── MEASURE PIPELINE ROW ───────────────────────────────────── */

const MeasurePipelineRow = ({ measure, onViewDetails }) => {
  const pipelineStep = getPipelineStep(measure);
  const isRecommendation = !!measure.IsRecommendation;
  const statusInfo = isRecommendation
    ? { label: "Shortlisted", color: "warning",   icon: faLightbulb  }
    : measure.CancelledUtc
      ? { label: "Cancelled",   color: "danger",   icon: faTimesCircle }
      : measure.SignedOffUtc
        ? { label: "Signed Off", color: "success",  icon: faCertificate }
        : (measure.InstallUtc || measure.HandoverUtc)
          ? { label: "Installed", color: "success",  icon: faCheckCircle }
          : Number(measure.StageCount) > 0
            ? { label: "In Progress", color: "primary", icon: faWrench     }
            : { label: "Submitted",   color: "info",    icon: faPaperPlane };
  const simple =
    typeof measure.SimpleMeasureDetails === "string"
      ? (() => { try { return JSON.parse(measure.SimpleMeasureDetails); } catch { return null; } })()
      : measure.SimpleMeasureDetails;
  const measureName = getMeasureDisplayName(measure, simple) || "Unnamed Measure";

  return (
    <Card className="pipeline-row-card mb-2 border-0 shadow-sm">
      <Card.Body className="py-2 px-3">
        <div className="d-flex align-items-center gap-3">
          {/* Icon — colour tracks pipeline step */}
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: 36, height: 36,
              backgroundColor:
                pipelineStep === -1 ? "#f8d7da"
                : pipelineStep >= 4 ? "#c8e6c9"
                : pipelineStep >= 3 ? "#d4edda"
                : pipelineStep >= 2 ? "#cce5ff"
                : pipelineStep >= 1 ? "#e3f2fd"
                : "#fff8e1",
            }}
          >
            <FontAwesomeIcon
              icon={
                pipelineStep === -1 ? faTimesCircle
                : pipelineStep >= 4 ? faCertificate
                : pipelineStep >= 3 ? faCheckCircle
                : pipelineStep >= 2 ? faWrench
                : pipelineStep >= 1 ? faPaperPlane
                : faLightbulb
              }
              style={{
                color:
                  pipelineStep === -1 ? "#721c24"
                  : pipelineStep >= 4 ? "#1b5e20"
                  : pipelineStep >= 3 ? "#155724"
                  : pipelineStep >= 2 ? "#004085"
                  : pipelineStep >= 1 ? "#0277bd"
                  : "#e65100",
                fontSize: "0.9rem",
              }}
            />
          </div>

          {/* Name & info */}
          <div className="flex-fill" style={{ minWidth: 0 }}>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-semibold small text-truncate" style={{ maxWidth: 260 }} title={measureName}>
                {measureName}
              </span>
              <Badge bg={statusInfo.color} style={{ fontSize: "0.6rem" }}>
                <FontAwesomeIcon icon={statusInfo.icon} className="me-1" />
                {statusInfo.label}
              </Badge>
            </div>

            {/* Metrics row */}
            <div className="d-flex flex-wrap gap-2 mt-1">
              {measure.InstallUtc && (
                <span className="measure-pill" style={{ fontSize: "0.7rem" }}>
                  <FontAwesomeIcon icon={faCalendarAlt} className="me-1 text-muted" />
                  {formatDate(measure.InstallUtc)}
                </span>
              )}
              {(measure.EvidenceCount ?? 0) > 0 && (
                <span className="measure-pill" style={{ fontSize: "0.7rem" }}>
                  <FontAwesomeIcon icon={faFileAlt} className="me-1 text-muted" />
                  {measure.EvidenceCount} evidence
                </span>
              )}
              {measure.LotName && (
                <span className="measure-pill" style={{ fontSize: "0.7rem" }}>
                  <FontAwesomeIcon icon={faTag} className="me-1 text-muted" />
                  {measure.LotName}
                </span>
              )}
              {(measure.DesignCompleted === true || measure.DesignCompleted === 1) && (
                <OverlayTrigger overlay={<Tooltip>Design completed</Tooltip>}>
                  <Badge bg="success" style={{ fontSize: "0.6rem" }}>Design ✓</Badge>
                </OverlayTrigger>
              )}
              {measure.EcoStatus && ECO_STATUS_MAP[measure.EcoStatus] && (
                <Badge bg={ECO_STATUS_MAP[measure.EcoStatus].color} style={{ fontSize: "0.6rem" }}>
                  {ECO_STATUS_MAP[measure.EcoStatus].label}
                </Badge>
              )}
            </div>
          </div>

          {/* Pipeline step indicator */}
          <PipelineStepIndicator currentStep={pipelineStep} />

          <Button
            variant="outline-primary"
            size="sm"
            className="flex-shrink-0"
            onClick={() => onViewDetails(measure)}
            style={{ fontSize: "0.75rem" }}
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />
            Details
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
};

/* ─── JOB PANEL ──────────────────────────────────────────────── */

const JobPanel = ({ job, onViewMeasure, instance }) => {
  const [expanded, setExpanded] = useState(true);
  const [jobInsights, setJobInsights] = useState(null);
  const [shortlistedMeasures, setShortlistedMeasures] = useState([]);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [insightDetails, setInsightDetails] = useState(null);
  const [insightDetailsLoading, setInsightDetailsLoading] = useState(false);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [shortlistedLoading, setShortlistedLoading] = useState(false);
  const [insightsLoaded, setInsightsLoaded] = useState(false);
  const [shortlistedLoaded, setShortlistedLoaded] = useState(false);

  // Auto-load job insights when panel expands
  useEffect(() => {
    if (!expanded || !job.jobId) return;
    if ((insightsLoaded && shortlistedLoaded) || insightsLoading || shortlistedLoading) return;

    const load = async () => {
      setInsightsLoading(true);
      setShortlistedLoading(true);
      try {
        if (instance) {
          const [insightsRes, recommendationsRes] = await Promise.allSettled([
            measuresApiService.getJobInsights(instance, job.jobId),
            measuresApiService.getJobRecommendations(instance, job.jobId),
          ]);

          if (insightsRes.status === "fulfilled") {
            setJobInsights(insightsRes.value?.data || null);
          } else {
            console.warn("Failed to load job insights:", insightsRes.reason);
            setJobInsights(null);
          }

          if (recommendationsRes.status === "fulfilled") {
            const recommendationRows = Array.isArray(recommendationsRes.value?.data)
              ? recommendationsRes.value.data
              : [];
            setShortlistedMeasures(recommendationRows);
          } else {
            console.warn("Failed to load job recommendations:", recommendationsRes.reason);
            setShortlistedMeasures([]);
          }
        } else {
          setJobInsights(null);
          setShortlistedMeasures([]);
        }
      } catch (err) {
        console.warn("Failed to load job insights/recommendations:", err);
        setJobInsights(null);
        setShortlistedMeasures([]);
      } finally {
        setInsightsLoading(false);
        setShortlistedLoading(false);
        setInsightsLoaded(true);
        setShortlistedLoaded(true);
      }
    };
    load();
  }, [expanded, insightsLoaded, shortlistedLoaded, insightsLoading, shortlistedLoading, job.jobId, instance]);

  const measuresForDisplay = useMemo(() => {
    const normalizeName = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const namesLikelySameMeasure = (left, right) => {
      if (!left || !right) return false;
      if (left === right) return true;
      if (left.includes(right) || right.includes(left)) return true;

      const leftTokens = left.split(" ").filter((t) => t.length > 2);
      const rightTokens = new Set(right.split(" ").filter((t) => t.length > 2));
      if (leftTokens.length === 0 || rightTokens.size === 0) return false;

      const overlap = leftTokens.filter((t) => rightTokens.has(t)).length;
      const minTokenCount = Math.max(2, Math.min(leftTokens.length, rightTokens.size));
      return overlap >= minTokenCount;
    };

    const actionedMeasures = job.measures.filter((m) => !m.IsJobOnly).map((m) => {
      const simple =
        typeof m.SimpleMeasureDetails === "string"
          ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
          : m.SimpleMeasureDetails;

      return {
        typeId: String(m.PasMeasureTypeLookupId || "").toLowerCase(),
        name: normalizeName(getMeasureDisplayName(m, simple)),
      };
    });

    const getMeasureKey = (m) => {
      const typeId = String(m.PasMeasureTypeLookupId || "").toLowerCase();
      if (typeId) return `type:${typeId}`;

      const simple =
        typeof m.SimpleMeasureDetails === "string"
          ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
          : m.SimpleMeasureDetails;
      const name = normalizeName(getMeasureDisplayName(m, simple) || m.MeasureModelName || m.MeasureName || m.PasMeasureName);
      if (name) return `name:${name}`;

      return `id:${m.Id || m.RecommendationId || Math.random().toString(36).slice(2)}`;
    };

    const chooseBetter = (current, candidate) => {
      const currentUpdated = new Date(current.UpdatedUtc || current.CreatedUtc || 0).getTime();
      const candidateUpdated = new Date(candidate.UpdatedUtc || candidate.CreatedUtc || 0).getTime();

      const currentCancelled = Number(current.MeasureStatus) === 5;
      const candidateCancelled = Number(candidate.MeasureStatus) === 5;

      // If one version is cancelled and it's the latest snapshot, surface cancelled.
      if (currentCancelled !== candidateCancelled) {
        if (candidateCancelled && candidateUpdated >= currentUpdated) return candidate;
        if (currentCancelled && currentUpdated >= candidateUpdated) return current;
      }

      const currentStep = getPipelineStep(current);
      const candidateStep = getPipelineStep(candidate);
      if (candidateStep !== currentStep) return candidateStep > currentStep ? candidate : current;

      return candidateUpdated > currentUpdated ? candidate : current;
    };

    const mergedByKey = new Map();

    job.measures.filter((m) => !m.IsJobOnly).forEach((m) => {
      const key = getMeasureKey(m);
      const existing = mergedByKey.get(key);
      if (!existing) {
        mergedByKey.set(key, { ...m });
        return;
      }

      const preferred = chooseBetter(existing, m);
      const other = preferred === existing ? m : existing;
      mergedByKey.set(key, {
        ...preferred,
        EvidenceCount: Math.max(Number(preferred.EvidenceCount || 0), Number(other.EvidenceCount || 0)),
        StageCount: Math.max(Number(preferred.StageCount || 0), Number(other.StageCount || 0)),
        SiteVisitCount: Math.max(Number(preferred.SiteVisitCount || 0), Number(other.SiteVisitCount || 0)),
      });
    });

    shortlistedMeasures
      .map((r) => {
        const recommendationName =
          r.MeasureModelName ||
          r.PasMeasureName ||
          r.MeasureName ||
          r.PasMeasureType ||
          "Shortlisted measure";

        return {
          ...r,
          Id: `rec-${job.jobId}-${r.Id}`,
          RecommendationId: r.Id,
          JobId: job.jobId,
          JobUId: job.jobUId,
          CompanyName: job.companyName,
          PasMeasureName: recommendationName,
          MeasureName: recommendationName,
          MeasureStatus: null,
          EvidenceCount: 0,
          StageCount: 0,
          SiteVisitCount: 0,
          IsRecommendation: true,
        };
      })
      .forEach((r) => {
        const typeId = String(r.PasMeasureTypeLookupId || "").toLowerCase();

        const recName = normalizeName(r.PasMeasureName || r.MeasureName);
        const matchingActioned = actionedMeasures.find((a) => {
          if (typeId && a.typeId && typeId === a.typeId) return true;
          return namesLikelySameMeasure(recName, a.name);
        });

        // If this recommendation already has an actioned/installed row, keep one line only.
        if (matchingActioned) return;

        const key = getMeasureKey(r);
        if (!mergedByKey.has(key)) {
          mergedByKey.set(key, r);
        }
      });

    return Array.from(mergedByKey.values());
  }, [job.measures, job.jobId, job.jobUId, job.companyName, shortlistedMeasures]);

  const panelLoading = insightsLoading || shortlistedLoading;

  // Funnel counts — date-first lifecycle classification
  const actionedMeasures = job.measures.filter((m) => !m.IsJobOnly);
  const actionedCount         = actionedMeasures.length;
  const cancelledCount        = actionedMeasures.filter((m) => m.CancelledUtc).length;
  const signedOffCount        = actionedMeasures.filter((m) => m.SignedOffUtc && !m.CancelledUtc).length;
  const activeInstalledCount  = actionedMeasures.filter((m) => (m.InstallUtc || m.HandoverUtc) && !m.SignedOffUtc && !m.CancelledUtc).length;
  const inProgressCount       = actionedMeasures.filter((m) => !m.CancelledUtc && !m.InstallUtc && !m.HandoverUtc && !m.SignedOffUtc && Number(m.StageCount) > 0).length;
  const submittedCount        = actionedMeasures.filter((m) => !m.CancelledUtc && !m.InstallUtc && !m.HandoverUtc && !m.SignedOffUtc && !(Number(m.StageCount) > 0)).length;

  // Shortlisted = recommendations not yet matched to an actioned measure (deduplicated by measuresForDisplay).
  // Using the raw insights total would over-count because it includes measures already actioned/installed/signed off.
  const shortlistedCount = !insightsLoaded ? null : measuresForDisplay.filter((m) => m.IsRecommendation).length;

  const appointmentsTotal = Number(jobInsights?.appointments?.total || 0);
  const intendedOutcomesTotal = Number(jobInsights?.intendedOutcomes?.total || 0);
  const totalImprovements = Number(jobInsights?.improvementMeasures?.total || 0);
  const latestAppointment = jobInsights?.appointments?.latest || null;
  const latestAppointmentDate = formatDate(latestAppointment?.AppointmentDateTime);
  const latestAppointmentStatus = latestAppointment
    ? (APPOINTMENT_STATUS_MAP[latestAppointment?.AppointmentStatus] || `Status ${latestAppointment?.AppointmentStatus ?? "Unknown"}`)
    : "";
  const latestAppointmentNotes = (latestAppointment?.Notes || "").trim();
  const latestAppointmentNotesShort = latestAppointmentNotes.length > 120
    ? `${latestAppointmentNotes.slice(0, 117)}...`
    : latestAppointmentNotes;
  const nextAppointmentLabel = formatDate(jobInsights?.appointments?.nextAppointmentUtc);
  const fuelSaving = jobInsights?.latestImprovementModel?.overallFuelBillSaving;
  const fallbackFuelSaving = Number(job.annualFuelSaving);
  const resolvedFuelSaving = fuelSaving != null && !isNaN(Number(fuelSaving))
    ? Number(fuelSaving)
    : (Number.isFinite(fallbackFuelSaving) ? fallbackFuelSaving : null);
  const hasInsights = appointmentsTotal > 0 || intendedOutcomesTotal > 0 || totalImprovements > 0 || (fuelSaving != null && !isNaN(Number(fuelSaving)));
  const dwpStatusNum = Number(job.dwpStatus);
  const shouldShowDwpWarning = [3, 4, 5].includes(dwpStatusNum);
  const hasAssessmentRef = String(job.assessmentRef || "").trim().length > 0;
  const residentNames = Array.isArray(job.residentNames)
    ? job.residentNames.filter(Boolean)
    : [];
  const residentNamesLabel = residentNames.length > 0
    ? residentNames.join(", ")
    : (job.primaryResidentName || "");
  const contributionValue = Number(job.customerContribution);
  const invoiceTotalValue = Number(job.invoiceTotal);

  const openInsightsModal = useCallback(async () => {
    setShowInsightsModal(true);
    if (insightDetails || insightDetailsLoading || !job.jobId || !instance) return;

    setInsightDetailsLoading(true);
    try {
      const res = await measuresApiService.getJobInsightsDetails(instance, job.jobId);
      setInsightDetails(res?.data || null);
    } catch (err) {
      console.warn("Failed to load job insight details:", err);
      setInsightDetails({
        appointments: [],
        intendedOutcomes: [],
        requestResponses: [],
        selectedImprovementMeasures: [],
      });
    } finally {
      setInsightDetailsLoading(false);
    }
  }, [insightDetails, insightDetailsLoading, job.jobId, instance]);

  return (
    <Card className="job-panel mb-3 border-0 shadow-sm">
      <Card.Header
        className="bg-white border-0 py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div className="job-icon-circle">
              <FontAwesomeIcon icon={faBriefcase} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-semibold">
                  Job {job.jobUId || job.jobId?.substring(0, 8)}
                </h6>
                {(() => {
                  const lc = job.jobLifecycleLabel;
                  const raw = JOB_STATUS_MAP[job.jobStatusId];
                  if (lc) {
                    return (
                      <>
                        <Badge bg={lc.color} className="px-2 py-1">{lc.label}</Badge>
                        {raw && raw.label !== lc.label && (
                          <Badge bg="light" text="dark" className="px-2 py-1 border" title="Raw system status">{raw.label}</Badge>
                        )}
                      </>
                    );
                  }
                  return getStatusBadge(job.jobStatusId, JOB_STATUS_MAP);
                })()}
                {job.lodgementStatus != null && getStatusBadge(job.lodgementStatus, LODGEMENT_STATUS_MAP)}
                {shouldShowDwpWarning && dwpStatusNum >= 0 && getStatusBadge(dwpStatusNum, DWP_STATUS_MAP)}
                {job.isOnHold && <Badge bg="warning" text="dark" className="px-2 py-1">On Hold</Badge>}
                {job.jobCancelledUtc && <Badge bg="danger" className="px-2 py-1">Job Cancelled</Badge>}
              </div>
              <div className="small text-muted mt-1">
                {job.externalCrmId && (
                  <span className="me-3">
                    External CRM ID: {job.externalCrmId}
                  </span>
                )}
                {job.companyName && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faBuilding} className="me-1" />
                    {job.companyName}
                  </span>
                )}
                {job.createdByName && (
                  <span>
                    <FontAwesomeIcon icon={faUser} className="me-1" />
                    {job.createdByName}
                  </span>
                )}
                {residentNamesLabel && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faHome} className="me-1" />
                    Residents: {residentNamesLabel}
                  </span>
                )}
                {hasAssessmentRef && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faCertificate} className="me-1" />
                    Assessment {job.assessmentRef}
                  </span>
                )}
                {Number.isFinite(contributionValue) && contributionValue > 0 && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faPoundSign} className="me-1" />
                    Customer contribution GBP {contributionValue.toLocaleString()}
                  </span>
                )}
                {Number.isFinite(invoiceTotalValue) && invoiceTotalValue > 0 && (
                  <span>
                    <FontAwesomeIcon icon={faFileAlt} className="me-1" />
                    Invoice total GBP {invoiceTotalValue.toLocaleString()}
                  </span>
                )}
                {Number.isFinite(Number(job.currentEnergyRating)) && Number.isFinite(Number(job.potentialEnergyRating)) && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faLeaf} className="me-1" />
                    SAP {Number(job.currentEnergyRating)} to {Number(job.potentialEnergyRating)}
                  </span>
                )}
                {(!Number.isFinite(Number(job.currentEnergyRating)) || !Number.isFinite(Number(job.potentialEnergyRating))) &&
                  Number.isFinite(Number(job.surveyCurrentSAP)) && Number.isFinite(Number(job.surveyPotentialSAP)) && (
                    <span className="me-3">
                      <FontAwesomeIcon icon={faLeaf} className="me-1" />
                      Survey SAP {Number(job.surveyCurrentSAP)} to {Number(job.surveyPotentialSAP)}
                    </span>
                  )}
                {job.surveyHasDampMouldIssue && (
                  <span className="me-3 text-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    Damp/Mould noted
                  </span>
                )}
                {job.surveyHasHHSRSIssue && (
                  <span className="me-3 text-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                    HHSRS issue noted
                  </span>
                )}
                {job.surveyWallType && (
                  <span className="me-3">
                    <FontAwesomeIcon icon={faTag} className="me-1" />
                    Wall type: {job.surveyWallType}
                  </span>
                )}
                {job.surveyBuiltForm && (
                  <span>
                    <FontAwesomeIcon icon={faHome} className="me-1" />
                    Built form: {job.surveyBuiltForm}
                  </span>
                )}
                {Number.isFinite(Number(job.projectStageCount)) && Number(job.projectStageCount) > 0 && (
                  <span>
                    <FontAwesomeIcon icon={faListAlt} className="me-1" />
                    Stages {Number(job.projectStageCount)}
                    {Number(job.pausedStageCount || 0) > 0 ? `, ${Number(job.pausedStageCount)} paused` : ""}
                    {Number(job.cancelledStageCount || 0) > 0 ? `, ${Number(job.cancelledStageCount)} cancelled` : ""}
                  </span>
                )}
              </div>
              {insightsLoaded && hasInsights && (
                <div className="small text-muted mt-1 d-flex flex-wrap gap-3">
                  {appointmentsTotal > 0 && (
                    <span>
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      {appointmentsTotal} appointments
                      {latestAppointmentDate !== "—" ? `, latest ${latestAppointmentDate}` : ""}
                      {latestAppointmentStatus ? ` (${latestAppointmentStatus})` : ""}
                      {nextAppointmentLabel !== "—" ? `, next ${nextAppointmentLabel}` : ""}
                    </span>
                  )}
                  {latestAppointmentNotesShort && (
                    <span>
                      <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                      Note: {latestAppointmentNotesShort}
                    </span>
                  )}
                  {intendedOutcomesTotal > 0 && (
                    <span>
                      <FontAwesomeIcon icon={faQuestionCircle} className="me-1" />
                      {intendedOutcomesTotal} intended outcomes
                    </span>
                  )}
                  {totalImprovements > 0 && (
                    <span>
                      <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                      Improvement model: {shortlistedCount} shortlisted from {totalImprovements}
                    </span>
                  )}
                  {resolvedFuelSaving != null && !isNaN(Number(resolvedFuelSaving)) && Number(resolvedFuelSaving) > 0 && (
                    <span>
                      <FontAwesomeIcon icon={faBolt} className="me-1" />
                      Potential saving £{Number(resolvedFuelSaving).toLocaleString()}/yr
                    </span>
                  )}
                  {instance && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="py-0 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInsightsModal();
                      }}
                      style={{ fontSize: "0.7rem" }}
                    >
                      <FontAwesomeIcon icon={faListAlt} className="me-1" />
                      View insights
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="text-muted"
          />
        </div>
      </Card.Header>

      {expanded && (
        <Card.Body className="pt-0 pb-3 px-3">
          {panelLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" variant="primary" />
              <p className="text-muted small mt-2">Loading job insights...</p>
            </div>
          ) : (
            <>
              {/* Funnel legend */}
              {measuresForDisplay.length > 0 && (
                <div className="d-flex align-items-center flex-wrap gap-2 mb-3 pb-2 border-bottom">
                  <span className="small text-muted fw-semibold me-1">Funnel:</span>
                  {[
                    { color: "#ff9800", label: "Shortlisted", count: insightsLoaded ? (shortlistedCount ?? 0) : null, loading: !insightsLoaded },
                    { color: "#0288d1", label: "Submitted",   count: submittedCount },
                    { color: "#1976d2", label: "In Progress", count: inProgressCount },
                    { color: "#388e3c", label: "Installed",   count: activeInstalledCount },
                    { color: "#2e7d32", label: "Signed Off",  count: signedOffCount },
                  ].map((s, i) => (
                    <React.Fragment key={s.label}>
                      {i > 0 && <FontAwesomeIcon icon={faArrowRight} className="text-muted" style={{ fontSize: "0.55rem" }} />}
                      <span className="d-flex align-items-center gap-1 small">
                        <span className="pipeline-legend-dot" style={{ backgroundColor: s.color, opacity: s.count === 0 ? 0.35 : 1 }} />
                        <span style={{ opacity: s.count === 0 ? 0.45 : 1 }}>
                          {s.label}{" "}
                          <strong>({s.loading ? <Spinner animation="border" style={{ width: 8, height: 8, borderWidth: 2 }} /> : s.count})</strong>
                        </span>
                      </span>
                    </React.Fragment>
                  ))}
                  {cancelledCount > 0 && (
                    <span className="d-flex align-items-center gap-1 small text-danger ms-1">
                      <FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: "0.65rem" }} />
                      <strong>{cancelledCount}</strong> cancelled
                    </span>
                  )}
                </div>
              )}

              {/* Measures list — actioned + shortlisted-not-actioned */}
              {measuresForDisplay.length > 0 && (
                <div className="mb-3">
                  {[...measuresForDisplay]
                    .sort((a, b) => {
                      const stepA = getPipelineStep(a);
                      const stepB = getPipelineStep(b);
                      return stepB - stepA; // installed (2) first, cancelled (-1) last
                    })
                    .map((m) => (
                      <MeasurePipelineRow
                        key={m.Id}
                        measure={m}
                        onViewDetails={onViewMeasure}
                      />
                    ))}
                </div>
              )}

              {measuresForDisplay.length === 0 && (
                <p className="text-muted small text-center py-3">No shortlisted or actioned measures on this job yet.</p>
              )}

              {/* Summary bar — sourced directly from MeasureSummary */}
              {job.measures.length > 0 && (() => {
                const activeMeasures = job.measures.filter(m => m.MeasureStatus !== 5);
                const installedMeasures = activeMeasures.filter(m => m.MeasureStatus >= 3);
                const totalCost = activeMeasures.reduce((s, m) => s + (Number(m.InstallationCost) || 0), 0);
                const installedCost = installedMeasures.reduce((s, m) => s + (Number(m.InstallationCost) || 0), 0);
                if (totalCost === 0 && installedCost === 0) return null;
                return (
                  <div className="d-flex flex-wrap gap-3 mt-2 p-2 rounded border" style={{ backgroundColor: "#f8f9fa" }}>
                    <span className="small fw-semibold text-muted">
                      <FontAwesomeIcon icon={faWrench} className="me-1" />
                      {actionedCount} actioned, {activeInstalledCount + signedOffCount} installed
                    </span>
                    {totalCost > 0 && (
                      <span className="small fw-semibold text-primary">
                        <FontAwesomeIcon icon={faPoundSign} className="me-1" />
                        £{installedCost.toLocaleString()} installed / £{totalCost.toLocaleString()} total
                      </span>
                    )}
                    {insightsLoaded && jobInsights?.latestImprovementModel?.overallFuelBillSaving > 0 && (
                      <span className="small fw-semibold text-success">
                        <FontAwesomeIcon icon={faBolt} className="me-1" />
                        Potential saving £{Number(jobInsights.latestImprovementModel.overallFuelBillSaving).toLocaleString()}/yr
                      </span>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </Card.Body>
      )}

      <Modal
        show={showInsightsModal}
        onHide={() => setShowInsightsModal(false)}
        size="lg"
        centered
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faListAlt} className="me-2" />
            Job Insights Detail: {job.jobUId || job.jobId?.substring(0, 8)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {insightDetailsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" variant="primary" />
              <p className="small text-muted mt-2 mb-0">Loading job insight details...</p>
            </div>
          ) : (
            <>
              <h6 className="fw-semibold mb-2">Appointments</h6>
              {(insightDetails?.appointments || []).length === 0 ? (
                <p className="small text-muted">No appointment records found.</p>
              ) : (
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Resource</th>
                        <th>Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(insightDetails?.appointments || []).map((a) => (
                        <tr key={a.AppointmentId}>
                          <td>{formatDate(a.AppointmentDateTime)}</td>
                          <td>{APPOINTMENT_STATUS_MAP[a.AppointmentStatus] ?? a.AppointmentStatus ?? "-"}</td>
                          <td>{APPOINTMENT_TYPE_MAP[a.AppointmentType] ?? a.AppointmentType ?? "-"}</td>
                          <td>{a.ResourceName || "-"}</td>
                          <td>{a.CompanyName || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h6 className="fw-semibold mb-2">Intended Outcomes</h6>
              {(insightDetails?.intendedOutcomes || []).length === 0 ? (
                <p className="small text-muted">No intended outcomes found.</p>
              ) : (
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th>Other Details</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(insightDetails?.intendedOutcomes || []).map((o) => (
                        <tr key={o.Id}>
                          <td>{o.IntendedOutcome ?? "-"}</td>
                          <td>{o.OtherDetails || "-"}</td>
                          <td>{formatDate(o.UpdatedUtc)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h6 className="fw-semibold mb-2">Improvement Model — Shortlisted Measures</h6>
              {(insightDetails?.selectedImprovementMeasures || []).length === 0 ? (
                <p className="small text-muted">No improvement measures found.</p>
              ) : (
                <div className="table-responsive mb-3">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Selected</th>
                        <th>Price</th>
                        <th>Fuel Saving</th>
                        <th>SAP Saving</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(insightDetails?.selectedImprovementMeasures || []).map((m) => (
                        <tr key={m.Id}>
                          <td>{m.Name || "-"}</td>
                          <td>{m.Selected ? "Yes" : "No"}</td>
                          <td>{m.Price != null ? `£${Number(m.Price).toLocaleString()}` : "-"}</td>
                          <td>{m.IndividualFuelBillSaving != null ? `£${Number(m.IndividualFuelBillSaving).toLocaleString()}` : "-"}</td>
                          <td>{m.SapScoreSaving != null ? Number(m.SapScoreSaving) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h6 className="fw-semibold mb-2">Improvement Request/Response Activity</h6>
              {(insightDetails?.requestResponses || []).length === 0 ? (
                <p className="small text-muted mb-0">No request/response activity found.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Request Type</th>
                        <th>Request Size</th>
                        <th>Response Size</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(insightDetails?.requestResponses || []).map((r) => (
                        <tr key={r.Id}>
                          <td>{r.JobImprovementModelRequestType ?? "-"}</td>
                          <td>{r.requestSize ?? 0}</td>
                          <td>{r.responseSize ?? 0}</td>
                          <td>{formatDate(r.UpdatedUtc)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>
    </Card>
  );
};

/* ─── PROPERTY CARD ──────────────────────────────────────────── */

const PropertyCard = ({ property, onViewMeasure, instance }) => {
  const [expanded, setExpanded] = useState(true);
  const jobs = Object.values(property.jobs);
  const totalMeasures = jobs.reduce((s, j) => s + j.measures.length, 0);

  return (
    <Card className="property-card mb-4 border-0 shadow">
      <Card.Header
        className="property-card-header py-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div className="property-icon-circle">
              <FontAwesomeIcon icon={faHome} />
            </div>
            <div>
              <h5 className="mb-0 fw-bold text-white">
                {property.address || "Unknown Address"}
              </h5>
              <span className="small" style={{ color: "rgba(255,255,255,0.8)" }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-1" />
                {property.postcode || ""}
                <span className="ms-3">
                  {jobs.length} job{jobs.length !== 1 && "s"} · {totalMeasures} measure
                  {totalMeasures !== 1 && "s"}
                </span>
              </span>
            </div>
          </div>
          <FontAwesomeIcon
            icon={expanded ? faChevronUp : faChevronDown}
            className="text-white"
          />
        </div>
      </Card.Header>
      {expanded && (
        <Card.Body className="p-3 bg-light">
          {jobs.map((job) => (
            <JobPanel key={job.jobId || job.jobUId} job={job} onViewMeasure={onViewMeasure} instance={instance} />
          ))}
        </Card.Body>
      )}
    </Card>
  );
};

/* ─── SUMMARY BAR ────────────────────────────────────────────── */

const SummaryBar = ({ totals, loadedData }) => {
  // Compute counts from whatever is currently loaded in the list
  const allJobs = Object.values(loadedData || {}).flatMap((p) => Object.values(p.jobs));
  const loadedMeasures = allJobs.reduce((s, j) => s + j.measures.length, 0);
  const hasVisibleResults = loadedMeasures > 0;

  // Prefer server-side totals (reflect full dataset); fall back to loaded counts
  const totalProperties = hasVisibleResults ? (totals?.totalProperties ?? Object.keys(loadedData || {}).length) : 0;
  const totalJobs       = hasVisibleResults ? (totals?.totalJobs       ?? allJobs.length) : 0;
  const totalMeasures   = hasVisibleResults ? (totals?.totalMeasures   ?? loadedMeasures) : 0;

  return (
    <Row className="mb-4 g-3">
      {[
        {
          label: "Properties",
          value: totalProperties.toLocaleString(),
          icon: faHome,
          color: "#667eea",
        },
        {
          label: "Jobs",
          value: totalJobs.toLocaleString(),
          icon: faBriefcase,
          color: "#764ba2",
        },
        {
          label: "Measures",
          value: totalMeasures.toLocaleString(),
          icon: faWrench,
          color: "#f093fb",
        },
      ].map((item) => (
        <Col key={item.label} sm={6} lg={4}>
          <Card className="border-0 shadow-sm summary-stat-card h-100">
            <Card.Body className="d-flex align-items-center gap-3 py-3">
              <div
                className="summary-icon-circle"
                style={{ backgroundColor: item.color + "22", color: item.color }}
              >
                <FontAwesomeIcon icon={item.icon} />
              </div>
              <div>
                <div className="small text-muted">{item.label}</div>
                <div className="fs-4 fw-bold">{item.value}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

/* ─── MAIN PAGE COMPONENT ────────────────────────────────────── */

const MeasuresTracker = () => {
  const { instance } = useMsal();
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [lodgementFilter, setLodgementFilter] = useState("");
  const [measureNameFilter, setMeasureNameFilter] = useState("");
  const [fundingCategoryFilter, setFundingCategoryFilter] = useState("");
  const [fundingNameFilter, setFundingNameFilter] = useState("");
  const [schemeFilter, setSchemeFilter] = useState("");
  const [onHoldFilter, setOnHoldFilter] = useState("");
  const [cancelledJobsFilter, setCancelledJobsFilter] = useState("");
  const [createdFromFilter, setCreatedFromFilter] = useState("");
  const [createdToFilter, setCreatedToFilter] = useState("");
  const [jobStatusFilter, setJobStatusFilter] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [pasVersionFilter, setPasVersionFilter] = useState("");
  const [uprnFilter, setUprnFilter] = useState("");
  const [assessmentRefFilter, setAssessmentRefFilter] = useState("");
  const [surveyDampMouldFilter, setSurveyDampMouldFilter] = useState("");
  const [surveyHhsrsFilter, setSurveyHhsrsFilter] = useState("");
  const [surveySapFilter, setSurveySapFilter] = useState("");
  const [surveyWallTypeFilter, setSurveyWallTypeFilter] = useState("");
  const [surveyBuiltFormFilter, setSurveyBuiltFormFilter] = useState("");
  const [selectedMeasure, setSelectedMeasure] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [useDemoData, setUseDemoData] = useState(true); // Start with demo data until backend is ready
  const [serverSearchMode, setServerSearchMode] = useState(false);
  const [overviewTotals, setOverviewTotals] = useState(null);
  const [jobStatusCounts, setJobStatusCounts] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const loadMoreSentinelRef = useRef(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const advancedFilterCount = useMemo(() => (
    [
      onHoldFilter,
      cancelledJobsFilter,
      createdFromFilter,
      createdToFilter,
      jobStatusFilter,
      jobTypeFilter,
      pasVersionFilter,
      uprnFilter,
      assessmentRefFilter,
      surveyDampMouldFilter,
      surveyHhsrsFilter,
      surveySapFilter,
      surveyWallTypeFilter,
      surveyBuiltFormFilter,
    ].filter((value) => String(value || "").trim() !== "").length
  ), [
    onHoldFilter,
    cancelledJobsFilter,
    createdFromFilter,
    createdToFilter,
    jobStatusFilter,
    jobTypeFilter,
    pasVersionFilter,
    uprnFilter,
    assessmentRefFilter,
    surveyDampMouldFilter,
    surveyHhsrsFilter,
    surveySapFilter,
    surveyWallTypeFilter,
    surveyBuiltFormFilter,
  ]);

  const buildServerFilters = useCallback((page, pageSize) => ({
    page,
    pageSize,
    ...(statusFilter ? { lifecycleStatus: statusFilter } : {}),
    ...(lodgementFilter !== "" ? { lodgementStatus: lodgementFilter } : {}),
    ...(measureNameFilter ? { pasMeasureName: measureNameFilter } : {}),
    ...(fundingCategoryFilter ? { fundingCategory: fundingCategoryFilter } : {}),
    ...(fundingNameFilter ? { fundingName: fundingNameFilter } : {}),
    ...(schemeFilter ? { schemeName: schemeFilter } : {}),
    ...(onHoldFilter !== "" ? { onHold: onHoldFilter } : {}),
    ...(cancelledJobsFilter !== "" ? { cancelledJobs: cancelledJobsFilter } : {}),
    ...(createdFromFilter ? { createdFrom: createdFromFilter } : {}),
    ...(createdToFilter ? { createdTo: createdToFilter } : {}),
    ...(jobStatusFilter !== "" ? { jobStatus: jobStatusFilter } : {}),
    ...(jobTypeFilter !== "" ? { jobType: jobTypeFilter } : {}),
    ...(pasVersionFilter ? { pasVersion: pasVersionFilter } : {}),
    ...(uprnFilter ? { uprn: uprnFilter } : {}),
    ...(assessmentRefFilter ? { assessmentRef: assessmentRefFilter } : {}),
    ...(surveyDampMouldFilter !== "" ? { surveyDampMould: surveyDampMouldFilter } : {}),
    ...(surveyHhsrsFilter !== "" ? { surveyHhsrs: surveyHhsrsFilter } : {}),
    ...(surveySapFilter !== "" ? { surveySap: surveySapFilter } : {}),
    ...(surveyWallTypeFilter ? { surveyWallType: surveyWallTypeFilter } : {}),
    ...(surveyBuiltFormFilter ? { surveyBuiltForm: surveyBuiltFormFilter } : {}),
  }), [
    statusFilter,
    lodgementFilter,
    measureNameFilter,
    fundingCategoryFilter,
    fundingNameFilter,
    schemeFilter,
    onHoldFilter,
    cancelledJobsFilter,
    createdFromFilter,
    createdToFilter,
    jobStatusFilter,
    jobTypeFilter,
    pasVersionFilter,
    uprnFilter,
    assessmentRefFilter,
    surveyDampMouldFilter,
    surveyHhsrsFilter,
    surveySapFilter,
    surveyWallTypeFilter,
    surveyBuiltFormFilter,
  ]);

  /* ── Fetch data ──────────────────────────────────── */
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const term = searchTerm.trim();
      const useServerSearch = term.length >= 2;
      const serverFilters = buildServerFilters(1, 100);
      const response = useServerSearch
        ? await measuresApiService.searchMeasures(instance, term, serverFilters)
        : await measuresApiService.getMeasures(instance, serverFilters);
      setServerSearchMode(useServerSearch);
      setCurrentPage(1);
      const payload = response?.data;
      if (payload?.totals) {
        setOverviewTotals(payload.totals);
      }
      setHasMore(1 < (payload?.totalPages || 1));
      const results = extractMeasuresArray(payload);
      // Parse JSON fields if they arrive as strings
      const parsed = results.map((r) => ({
        ...r,
        SimpleMeasureDetails: parseJsonField(r.SimpleMeasureDetails),
        DefaultMeasureDetails: parseJsonField(r.DefaultMeasureDetails),
        RawMeasureDetails: parseJsonField(r.RawMeasureDetails),
      }));
      setRawData(parsed);
      setUseDemoData(false);
      if (parsed.length === 0) {
        setError(null); // empty is valid — show the empty state, not demo data
      }
    } catch (err) {
      console.error("Failed to fetch measures:", err);
      setError("Could not load measures. Showing demo data.");
      setUseDemoData(true);
      setServerSearchMode(false);
      setOverviewTotals(null);
    } finally {
      setLoading(false);
    }
  }, [instance, searchTerm, buildServerFilters]);

  /* ── Fetch supplementary overview once on mount ───────── */
  useEffect(() => {
    (async () => {
      try {
        await measuresApiService.getInsightsOverview(instance);
      } catch {
        // supplementary dashboard stats — silently ignore on failure
      }
    })();
  }, [instance]);

  useEffect(() => {
    (async () => {
      try {
        const response = await measuresApiService.getJobStatusCounts(instance);
        const counts = Array.isArray(response?.data) ? response.data : [];
        setJobStatusCounts(Object.fromEntries(counts.map((row) => [Number(row.JobStatusId), Number(row.Total || 0)])));
      } catch (error) {
        console.error('Could not load PAS Hub job-status totals:', error);
      }
    })();
  }, [instance]);

  /* ── Append next page of results ────────────────────── */
  const loadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const term = searchTerm.trim();
      const nextPage = currentPage + 1;
      const serverFilters = buildServerFilters(nextPage, 100);
      const response = term.length >= 2
        ? await measuresApiService.searchMeasures(instance, term, serverFilters)
        : await measuresApiService.getMeasures(instance, serverFilters);
      const payload = response?.data;
      if (payload?.totals) {
        setOverviewTotals(payload.totals);
      }
      const newResults = extractMeasuresArray(payload);
      const parsed = newResults.map((r) => ({
        ...r,
        SimpleMeasureDetails: parseJsonField(r.SimpleMeasureDetails),
        DefaultMeasureDetails: parseJsonField(r.DefaultMeasureDetails),
        RawMeasureDetails: parseJsonField(r.RawMeasureDetails),
      }));
      setRawData((prev) => [...prev, ...parsed]);
      setCurrentPage(nextPage);
      setHasMore(nextPage < (payload?.totalPages || 1));
    } catch (e) {
      console.error('loadMore failed:', e);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, currentPage, searchTerm, instance, buildServerFilters]);

  useEffect(() => {
    const t = setTimeout(() => {
      fetchData();
    }, 600);
    return () => clearTimeout(t);
  }, [fetchData]);

  // Auto-load next page when the bottom sentinel enters the viewport.
  useEffect(() => {
    if (!loadMoreSentinelRef.current || useDemoData || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin: "250px",
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [loadMore, hasMore, useDemoData]);

  /* ── Apply filters ─────────────────────────────────── */
  const sourceData = useDemoData ? DEMO_DATA : rawData;

  const fundingCategoryOptions = useMemo(() => {
    return [...new Set(sourceData.map((m) => getFundingCategoryLabel(m)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  const fundingNameOptions = useMemo(() => {
    return [...new Set(sourceData.map((m) => getFundingNameLabel(m)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  const schemeOptions = useMemo(() => {
    return [...new Set(sourceData.map((m) => getSchemeLabel(m)).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  const pasVersionOptions = useMemo(() => {
    return [...new Set(sourceData.map((m) => String(m.PasVersion || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  const jobTypeOptions = useMemo(() => {
    return [...new Set(sourceData.map((m) => String(m.JobTypeId ?? "")).filter((v) => v !== ""))]
      .sort((a, b) => Number(a) - Number(b));
  }, [sourceData]);

  const measureNameOptions = useMemo(() => {
    return [...new Set(
      sourceData
        .map((m) => {
          const smd = typeof m.SimpleMeasureDetails === "string"
            ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
            : m.SimpleMeasureDetails;
          return getMeasureDisplayName(m, smd) || m.PasMeasureName || m.MeasureName || "";
        })
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
  }, [sourceData]);

  const filteredData = useMemo(() => {
    let items = [...sourceData];

    if (searchTerm && !(serverSearchMode && !useDemoData)) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(
        (m) => {
          const smd = typeof m.SimpleMeasureDetails === "string"
            ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
            : m.SimpleMeasureDetails;
          const mName = getMeasureDisplayName(m, smd) || "";
          return (
            (m.SingleLineAddress || "").toLowerCase().includes(lower) ||
            (m.AddressPostcode || "").toLowerCase().includes(lower) ||
            mName.toLowerCase().includes(lower) ||
            (m.JobUId || "").toLowerCase().includes(lower) ||
            (m.CompanyName || "").toLowerCase().includes(lower)
          );
        }
      );
    }
    if (statusFilter) {
      items = items.filter((m) => getMeasureLifecycleLabel(m) === statusFilter);
    }
    if (lodgementFilter !== "") {
      items = items.filter((m) => String(m.LodgementStatus ?? "") === lodgementFilter);
    }
    if (measureNameFilter) {
      items = items.filter((m) => {
        const smd = typeof m.SimpleMeasureDetails === "string"
          ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
          : m.SimpleMeasureDetails;
        const displayName = getMeasureDisplayName(m, smd) || m.PasMeasureName || m.MeasureName || "";
        return displayName === measureNameFilter;
      });
    }
    if (fundingCategoryFilter) {
      items = items.filter((m) => getFundingCategoryLabel(m) === fundingCategoryFilter);
    }
    if (fundingNameFilter) {
      items = items.filter((m) => getFundingNameLabel(m) === fundingNameFilter);
    }
    if (schemeFilter) {
      items = items.filter((m) => getSchemeLabel(m) === schemeFilter);
    }
    if (onHoldFilter !== "") {
      items = items.filter((m) => (onHoldFilter === "true" ? !!m.OnHoldUtc : !m.OnHoldUtc));
    }
    if (cancelledJobsFilter !== "") {
      items = items.filter((m) => (cancelledJobsFilter === "true" ? !!m.CancelledUtc : !m.CancelledUtc));
    }
    if (createdFromFilter) {
      const from = new Date(createdFromFilter);
      if (!Number.isNaN(from.getTime())) {
        items = items.filter((m) => {
          const created = new Date(m.CreatedUtc);
          return !Number.isNaN(created.getTime()) && created >= from;
        });
      }
    }
    if (createdToFilter) {
      const to = new Date(createdToFilter);
      if (!Number.isNaN(to.getTime())) {
        const end = new Date(to);
        end.setDate(end.getDate() + 1);
        items = items.filter((m) => {
          const created = new Date(m.CreatedUtc);
          return !Number.isNaN(created.getTime()) && created < end;
        });
      }
    }
    if (jobStatusFilter !== "") {
      items = items.filter((m) => String(m.JobStatusId ?? "") === jobStatusFilter);
    }
    if (jobTypeFilter !== "") {
      items = items.filter((m) => String(m.JobTypeId ?? "") === jobTypeFilter);
    }
    if (pasVersionFilter) {
      items = items.filter((m) => String(m.PasVersion || "") === pasVersionFilter);
    }
    if (uprnFilter) {
      const term = uprnFilter.toLowerCase();
      items = items.filter((m) => String(m.Uprn || "").toLowerCase().includes(term));
    }
    if (assessmentRefFilter) {
      const term = assessmentRefFilter.toLowerCase();
      items = items.filter((m) => String(m.AssessmentRef || "").toLowerCase().includes(term));
    }
    if (surveyDampMouldFilter !== "") {
      items = items.filter((m) => (surveyDampMouldFilter === "true" ? !!m.SurveyHasDampMouldIssue : !m.SurveyHasDampMouldIssue));
    }
    if (surveyHhsrsFilter !== "") {
      items = items.filter((m) => (surveyHhsrsFilter === "true" ? !!m.SurveyHasHHSRSIssue : !m.SurveyHasHHSRSIssue));
    }
    if (surveySapFilter !== "") {
      items = items.filter((m) => {
        const hasSap = Number.isFinite(Number(m.SurveyCurrentSAP)) || Number.isFinite(Number(m.SurveyPotentialSAP));
        return surveySapFilter === "true" ? hasSap : !hasSap;
      });
    }
    if (surveyWallTypeFilter) {
      const term = surveyWallTypeFilter.toLowerCase();
      items = items.filter((m) => String(m.SurveyWallType || "").toLowerCase().includes(term));
    }
    if (surveyBuiltFormFilter) {
      const term = surveyBuiltFormFilter.toLowerCase();
      items = items.filter((m) => String(m.SurveyBuiltForm || "").toLowerCase().includes(term));
    }
    return items;
  }, [sourceData, searchTerm, statusFilter, lodgementFilter, measureNameFilter, fundingCategoryFilter, fundingNameFilter, schemeFilter, onHoldFilter, cancelledJobsFilter, createdFromFilter, createdToFilter, jobStatusFilter, jobTypeFilter, pasVersionFilter, uprnFilter, assessmentRefFilter, surveyDampMouldFilter, surveyHhsrsFilter, surveySapFilter, surveyWallTypeFilter, surveyBuiltFormFilter, serverSearchMode, useDemoData]);

  const groupedData = useMemo(() => groupByProperty(filteredData), [filteredData]);

  /* ── Excel Export ──────────────────────────────────── */
  /**
   * Fetch ALL pages from the backend using the current search term,
   * then apply the status filter client-side — same as the UI but
   * without the page limit so the export is always complete.
   */
  const fetchAllForExport = useCallback(async () => {
    const EXPORT_PAGE_SIZE = 500;
    const term = searchTerm.trim();
    const allRows = [];
    let page = 1;
    let totalPages = 1;

    do {
      const response = term.length >= 2
        ? await measuresApiService.searchMeasures(instance, term, buildServerFilters(page, EXPORT_PAGE_SIZE))
        : await measuresApiService.getMeasures(instance, buildServerFilters(page, EXPORT_PAGE_SIZE));
      const payload = response?.data;
      totalPages = payload?.totalPages || 1;
      const results = extractMeasuresArray(payload);
      const parsed = results.map((r) => ({
        ...r,
        SimpleMeasureDetails: parseJsonField(r.SimpleMeasureDetails),
        DefaultMeasureDetails: parseJsonField(r.DefaultMeasureDetails),
        RawMeasureDetails: parseJsonField(r.RawMeasureDetails),
      }));
      allRows.push(...parsed);
      page += 1;
    } while (page <= totalPages);

    // Jobs with no measure rows (leads, assessments booked) never appear in the
    // measure pages above; with no filters active they must still be exported.
    const noFiltersActive = term.length < 2 && [
      statusFilter, lodgementFilter, measureNameFilter, fundingCategoryFilter, fundingNameFilter,
      schemeFilter, onHoldFilter, cancelledJobsFilter, createdFromFilter, createdToFilter,
      jobStatusFilter, jobTypeFilter, pasVersionFilter, uprnFilter, assessmentRefFilter,
      surveyDampMouldFilter, surveyHhsrsFilter, surveySapFilter, surveyWallTypeFilter, surveyBuiltFormFilter,
    ].every((v) => v === "" || v === null || v === undefined);
    if (noFiltersActive) {
      let jobPage = 1;
      let jobTotalPages = 1;
      do {
        const response = await measuresApiService.getMeasures(instance, { jobsWithoutMeasures: true, page: jobPage, pageSize: EXPORT_PAGE_SIZE });
        const payload = response?.data;
        jobTotalPages = payload?.totalPages || 1;
        allRows.push(...extractMeasuresArray(payload));
        jobPage += 1;
      } while (jobPage <= jobTotalPages);
    }

    // Apply the status filter exactly as the UI does
    const filtered = statusFilter
      ? allRows.filter((m) => getMeasureLifecycleLabel(m) === statusFilter)
      : allRows;

    const lodgementFiltered = lodgementFilter === ""
      ? filtered
      : filtered.filter((m) => String(m.LodgementStatus ?? "") === lodgementFilter);

    const measureNameFiltered = measureNameFilter
      ? lodgementFiltered.filter((m) => {
          const smd = typeof m.SimpleMeasureDetails === "string"
            ? (() => { try { return JSON.parse(m.SimpleMeasureDetails); } catch { return null; } })()
            : m.SimpleMeasureDetails;
          const displayName = getMeasureDisplayName(m, smd) || m.PasMeasureName || m.MeasureName || "";
          return displayName === measureNameFilter;
        })
      : lodgementFiltered;

    const fundingCategoryFiltered = fundingCategoryFilter
      ? measureNameFiltered.filter((m) => getFundingCategoryLabel(m) === fundingCategoryFilter)
      : measureNameFiltered;

    const fundingNameFiltered = fundingNameFilter
      ? fundingCategoryFiltered.filter((m) => getFundingNameLabel(m) === fundingNameFilter)
      : fundingCategoryFiltered;

    const schemeFiltered = schemeFilter
      ? fundingNameFiltered.filter((m) => getSchemeLabel(m) === schemeFilter)
      : fundingNameFiltered;

    const onHoldFiltered = onHoldFilter === ""
      ? schemeFiltered
      : schemeFiltered.filter((m) => (onHoldFilter === "true" ? !!m.OnHoldUtc : !m.OnHoldUtc));

    const cancelledJobsFiltered = cancelledJobsFilter === ""
      ? onHoldFiltered
      : onHoldFiltered.filter((m) => (cancelledJobsFilter === "true" ? !!m.CancelledUtc : !m.CancelledUtc));

    const createdFromApplied = createdFromFilter
      ? cancelledJobsFiltered.filter((m) => {
          const created = new Date(m.CreatedUtc);
          const from = new Date(createdFromFilter);
          return !Number.isNaN(created.getTime()) && !Number.isNaN(from.getTime()) && created >= from;
        })
      : cancelledJobsFiltered;

    const createdToApplied = createdToFilter
      ? createdFromApplied.filter((m) => {
          const created = new Date(m.CreatedUtc);
          const to = new Date(createdToFilter);
          if (Number.isNaN(created.getTime()) || Number.isNaN(to.getTime())) return false;
          const end = new Date(to);
          end.setDate(end.getDate() + 1);
          return created < end;
        })
      : createdFromApplied;

    const jobStatusApplied = jobStatusFilter !== ""
      ? createdToApplied.filter((m) => String(m.JobStatusId ?? "") === jobStatusFilter)
      : createdToApplied;

    const jobTypeApplied = jobTypeFilter !== ""
      ? jobStatusApplied.filter((m) => String(m.JobTypeId ?? "") === jobTypeFilter)
      : jobStatusApplied;

    const pasVersionApplied = pasVersionFilter
      ? jobTypeApplied.filter((m) => String(m.PasVersion || "") === pasVersionFilter)
      : jobTypeApplied;

    const uprnApplied = uprnFilter
      ? pasVersionApplied.filter((m) => String(m.Uprn || "").toLowerCase().includes(uprnFilter.toLowerCase()))
      : pasVersionApplied;

    const assessmentRefApplied = assessmentRefFilter
      ? uprnApplied.filter((m) => String(m.AssessmentRef || "").toLowerCase().includes(assessmentRefFilter.toLowerCase()))
      : uprnApplied;

    const surveyDampMouldApplied = surveyDampMouldFilter !== ""
      ? assessmentRefApplied.filter((m) => (surveyDampMouldFilter === "true" ? !!m.SurveyHasDampMouldIssue : !m.SurveyHasDampMouldIssue))
      : assessmentRefApplied;

    const surveyHhsrsApplied = surveyHhsrsFilter !== ""
      ? surveyDampMouldApplied.filter((m) => (surveyHhsrsFilter === "true" ? !!m.SurveyHasHHSRSIssue : !m.SurveyHasHHSRSIssue))
      : surveyDampMouldApplied;

    const surveySapApplied = surveySapFilter !== ""
      ? surveyHhsrsApplied.filter((m) => {
          const hasSap = Number.isFinite(Number(m.SurveyCurrentSAP)) || Number.isFinite(Number(m.SurveyPotentialSAP));
          return surveySapFilter === "true" ? hasSap : !hasSap;
        })
      : surveyHhsrsApplied;

    const surveyWallApplied = surveyWallTypeFilter
      ? surveySapApplied.filter((m) => String(m.SurveyWallType || "").toLowerCase().includes(surveyWallTypeFilter.toLowerCase()))
      : surveySapApplied;

    const surveyBuiltFormApplied = surveyBuiltFormFilter
      ? surveyWallApplied.filter((m) => String(m.SurveyBuiltForm || "").toLowerCase().includes(surveyBuiltFormFilter.toLowerCase()))
      : surveyWallApplied;

    return groupByProperty(surveyBuiltFormApplied);
  }, [instance, searchTerm, statusFilter, lodgementFilter, measureNameFilter, fundingCategoryFilter, fundingNameFilter, schemeFilter, onHoldFilter, cancelledJobsFilter, createdFromFilter, createdToFilter, jobStatusFilter, jobTypeFilter, pasVersionFilter, uprnFilter, assessmentRefFilter, surveyDampMouldFilter, surveyHhsrsFilter, surveySapFilter, surveyWallTypeFilter, surveyBuiltFormFilter, buildServerFilters]);

  const handleExportData = useCallback(async (exportType) => {
    setIsExporting(true);
    setExportError(null);
    try {
      const [{ utils, writeFile }, exportGroupedData] = await Promise.all([
        import("xlsx"),
        fetchAllForExport(),
      ]);
      const buildSheet = ({ rows, cols }) => {
        const ws = utils.aoa_to_sheet(rows);
        ws["!cols"] = cols;
        return ws;
      };
      const fileName = `measures-tracker-export-${new Date().toISOString().split("T")[0]}`;
      switch (exportType) {
        case "job-summary": {
          const wb = utils.book_new();
          utils.book_append_sheet(wb, buildSheet(exportJobSummary(exportGroupedData)), "Summary");
          writeFile(wb, `${fileName}-job-summary.xlsx`);
          break;
        }
        case "measures": {
          const wb = utils.book_new();
          utils.book_append_sheet(wb, buildSheet(exportMeasures(exportGroupedData)), "Measures");
          writeFile(wb, `${fileName}-measures.xlsx`);
          break;
        }
        case "appointments": {
          const wb = utils.book_new();
          utils.book_append_sheet(wb, buildSheet(exportAppointments(exportGroupedData)), "Appointments");
          writeFile(wb, `${fileName}-appointments.xlsx`);
          break;
        }
        default: {
          const wb = utils.book_new();
          utils.book_append_sheet(wb, buildSheet(exportJobSummary(exportGroupedData)), "Job Summary");
          utils.book_append_sheet(wb, buildSheet(exportMeasures(exportGroupedData)), "Measures");
          utils.book_append_sheet(wb, buildSheet(exportAppointments(exportGroupedData)), "Appointments");
          writeFile(wb, `${fileName}-full.xlsx`);
          break;
        }
      }
    } catch (err) {
      console.error("Export failed:", err);
      setExportError("Export failed — please try again.");
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllForExport]);

  /* ── Handlers ──────────────────────────────────────── */
  const handleViewDetails = (measure) => {
    setSelectedMeasure(measure);
    setShowModal(true);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setLodgementFilter("");
    setMeasureNameFilter("");
    setFundingCategoryFilter("");
    setFundingNameFilter("");
    setSchemeFilter("");
    setOnHoldFilter("");
    setCancelledJobsFilter("");
    setCreatedFromFilter("");
    setCreatedToFilter("");
    setJobStatusFilter("");
    setJobTypeFilter("");
    setPasVersionFilter("");
    setUprnFilter("");
    setAssessmentRefFilter("");
    setSurveyDampMouldFilter("");
    setSurveyHhsrsFilter("");
    setSurveySapFilter("");
    setSurveyWallTypeFilter("");
    setSurveyBuiltFormFilter("");
  };

  /* ── Render ────────────────────────────────────────── */
  return (
    <Container fluid className="measures-tracker py-4 px-lg-5">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-1">
            <FontAwesomeIcon icon={faClipboardList} className="me-2 text-primary" />
            Measures Tracker
          </h3>
          <p className="text-muted mb-0 small">
            Track properties, jobs, and installed measures in one view.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {useDemoData && (
            <Badge bg="warning" text="dark" className="px-3 py-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
              Demo Data — backend not connected
            </Badge>
          )}
          {!useDemoData && (
            <>
              <Badge bg="secondary" className="px-3 py-2" title="Counted directly from non-cancelled PAS Hub jobs, including jobs without measures">
                Active leads: {jobStatusCounts[7] ?? '...'}
              </Badge>
              <Dropdown>
                <Dropdown.Toggle
                  variant="outline-success"
                  size="sm"
                  disabled={isExporting}
                  id="export-dropdown"
                >
                  {isExporting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Exporting…
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFileDownload} className="me-1" />
                      Export
                    </>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => handleExportData("job-summary")} disabled={isExporting}>
                    <FontAwesomeIcon icon={faBriefcase} className="me-2" />
                    Job Summary
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportData("measures")} disabled={isExporting}>
                    <FontAwesomeIcon icon={faWrench} className="me-2" />
                    Measures Detail
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleExportData("appointments")} disabled={isExporting}>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                    Appointments
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleExportData("full")} disabled={isExporting}>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    Full Export (All Sheets)
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              {exportError && (
                <span className="text-danger small">{exportError}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body className="py-3">
          <Form onSubmit={handleSearch}>
            <Row className="g-2 align-items-end">
              <Col md={6} lg={5}>
                <InputGroup>
                  <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by address, postcode, measure, or job ID…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3} lg={3}>
                <InputGroup>
                  <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon={faFilter} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    {["Cancelled", "Signed Off", "Installed", "New", "RC Instructed", "Awaiting Installation", "Installation Booked", "Awaiting Lodgement", "Completed"].map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                  </Form.Select>
                </InputGroup>
              </Col>
              <Col md={3} lg={2}>
                <Form.Select
                  value={lodgementFilter}
                  onChange={(e) => setLodgementFilter(e.target.value)}
                >
                  <option value="">All Lodgement</option>
                  {Object.entries(LODGEMENT_STATUS_MAP).map(([value, entry]) => (
                    <option key={value} value={value}>
                      {entry.label}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3} lg={2} className="d-flex gap-2">
                <Button type="submit" variant="primary" className="flex-fill">
                  <FontAwesomeIcon icon={faSearch} className="me-1" />
                  Search
                </Button>
                {(searchTerm || statusFilter || lodgementFilter || measureNameFilter || fundingCategoryFilter || fundingNameFilter || schemeFilter || onHoldFilter || cancelledJobsFilter || createdFromFilter || createdToFilter || jobStatusFilter || jobTypeFilter || pasVersionFilter || uprnFilter || assessmentRefFilter || surveyDampMouldFilter || surveyHhsrsFilter || surveySapFilter || surveyWallTypeFilter || surveyBuiltFormFilter) && (
                  <Button variant="outline-secondary" onClick={clearFilters}>
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </Col>
            </Row>
            <Row className="g-2 align-items-end mt-1">
              <Col md={3}>
                <Form.Select value={measureNameFilter} onChange={(e) => setMeasureNameFilter(e.target.value)}>
                  <option value="">All Measure Names</option>
                  {measureNameOptions.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select value={fundingCategoryFilter} onChange={(e) => setFundingCategoryFilter(e.target.value)}>
                  <option value="">All Funding Categories</option>
                  {fundingCategoryOptions.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select value={fundingNameFilter} onChange={(e) => setFundingNameFilter(e.target.value)}>
                  <option value="">All Funding Names</option>
                  {fundingNameOptions.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select value={schemeFilter} onChange={(e) => setSchemeFilter(e.target.value)}>
                  <option value="">All Schemes</option>
                  {schemeOptions.map((label) => (
                    <option key={label} value={label}>{label}</option>
                  ))}
                </Form.Select>
              </Col>
            </Row>
            <div className="mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowAdvancedFilters((prev) => !prev)}
                aria-controls="advanced-filters-panel"
                aria-expanded={showAdvancedFilters}
              >
                <FontAwesomeIcon icon={showAdvancedFilters ? faChevronUp : faChevronDown} className="me-1" />
                Advanced Filters
                {advancedFilterCount > 0 ? ` (${advancedFilterCount} active)` : ""}
              </Button>
            </div>
            <Collapse in={showAdvancedFilters}>
              <div id="advanced-filters-panel" className="mt-2 p-2 border rounded bg-light">
                <Row className="g-2 align-items-end">
                  <Col md={3}>
                    <Form.Select value={onHoldFilter} onChange={(e) => setOnHoldFilter(e.target.value)}>
                      <option value="">On-Hold: Any</option>
                      <option value="true">On-Hold Only</option>
                      <option value="false">Not On-Hold</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={cancelledJobsFilter} onChange={(e) => setCancelledJobsFilter(e.target.value)}>
                      <option value="">Cancelled Jobs: Any</option>
                      <option value="true">Cancelled Only</option>
                      <option value="false">Not Cancelled</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Job Created From</Form.Label>
                    <Form.Control type="date" value={createdFromFilter} onChange={(e) => setCreatedFromFilter(e.target.value)} />
                  </Col>
                  <Col md={3}>
                    <Form.Label className="small text-muted mb-1">Job Created To</Form.Label>
                    <Form.Control type="date" value={createdToFilter} onChange={(e) => setCreatedToFilter(e.target.value)} />
                  </Col>
                </Row>
                <Row className="g-2 align-items-end mt-1">
                  <Col md={3}>
                    <Form.Select value={jobStatusFilter} onChange={(e) => setJobStatusFilter(e.target.value)}>
                      <option value="">All Job Statuses</option>
                      {Object.entries(JOB_STATUS_MAP).map(([value, entry]) => (
                        <option key={value} value={value}>{entry.label}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={jobTypeFilter} onChange={(e) => setJobTypeFilter(e.target.value)}>
                      <option value="">All Job Types</option>
                      {jobTypeOptions.map((value) => (
                        <option key={value} value={value}>Job Type {value}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={pasVersionFilter} onChange={(e) => setPasVersionFilter(e.target.value)}>
                      <option value="">All PAS Versions</option>
                      {pasVersionOptions.map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      type="text"
                      placeholder="UPRN"
                      value={uprnFilter}
                      onChange={(e) => setUprnFilter(e.target.value)}
                    />
                  </Col>
                </Row>
                <Row className="g-2 align-items-end mt-1">
                  <Col md={3}>
                    <Form.Control
                      type="text"
                      placeholder="Assessment Ref"
                      value={assessmentRefFilter}
                      onChange={(e) => setAssessmentRefFilter(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Select value={surveyDampMouldFilter} onChange={(e) => setSurveyDampMouldFilter(e.target.value)}>
                      <option value="">Survey Damp/Mould: Any</option>
                      <option value="true">Survey Damp/Mould: Yes</option>
                      <option value="false">Survey Damp/Mould: No</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={surveyHhsrsFilter} onChange={(e) => setSurveyHhsrsFilter(e.target.value)}>
                      <option value="">Survey HHSRS/Hazard: Any</option>
                      <option value="true">Survey HHSRS/Hazard: Yes</option>
                      <option value="false">Survey HHSRS/Hazard: No</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select value={surveySapFilter} onChange={(e) => setSurveySapFilter(e.target.value)}>
                      <option value="">Survey SAP Present: Any</option>
                      <option value="true">Survey SAP Present: Yes</option>
                      <option value="false">Survey SAP Present: No</option>
                    </Form.Select>
                  </Col>
                </Row>
                <Row className="g-2 align-items-end mt-1">
                  <Col md={3}>
                    <Form.Control
                      type="text"
                      placeholder="Survey Wall Type contains"
                      value={surveyWallTypeFilter}
                      onChange={(e) => setSurveyWallTypeFilter(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Control
                      type="text"
                      placeholder="Survey Built Form contains"
                      value={surveyBuiltFormFilter}
                      onChange={(e) => setSurveyBuiltFormFilter(e.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            </Collapse>
          </Form>
        </Card.Body>
      </Card>

      {/* Error Banner */}
      {error && (
        <Alert variant="warning" dismissible onClose={() => setError(null)} className="mb-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading measures…</p>
        </div>
      )}

      {/* Content */}
      {!loading && (
        <>
          <SummaryBar totals={!useDemoData ? overviewTotals : null} loadedData={groupedData} />


          {Object.keys(groupedData).length === 0 ? (
            <Card className="border-0 shadow-sm text-center py-5">
              <Card.Body>
                <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                <h5 className="text-muted">No measures found</h5>
                <p className="text-muted small">Try adjusting your search or filters.</p>
              </Card.Body>
            </Card>
          ) : (
            <>
              {Object.entries(groupedData).map(([key, property]) => (
                <PropertyCard
                  key={key}
                  property={property}
                  onViewMeasure={handleViewDetails}
                  instance={instance}
                />
              ))}
              {hasMore && !useDemoData && (
                <div ref={loadMoreSentinelRef} className="text-center mt-4 mb-2 py-2">
                  {isLoadingMore ? (
                    <span className="text-muted small">
                      <Spinner animation="border" size="sm" className="me-2" />Loading more…
                    </span>
                  ) : (
                    <span className="text-muted small">Scroll to load more</span>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Detail Modal */}
      <MeasureDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        measure={selectedMeasure}
        instance={instance}
      />
    </Container>
  );
};

export default MeasuresTracker;